import { QueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { generatePath } from 'react-router-dom';
import { toast } from 'sonner';
import i18n from '../app/i18n';
import { httpClient } from './httpClient.config';
import { Endpoint } from '@/Config/api/endpoint';
import { configureEntryOperationsQueryClient } from '@/Config/api/entryOperationsQueryClient.config';
import { QueryKey } from '@/Config/api/queryKey';
import {
  ActionDto,
  AIActionUsageStatsResponseDto,
  HttpActionDto,
  HttpActionFromTextDto,
  ObservedMetadataKeyDto,
} from '@/Domain/api/Action/dto/Action.dto';
import { CampaignDto } from '@/Domain/api/Action/dto/Campaign.dto';
import {
  DiscordTemplateGuildDto,
  DiscordTemplateGuildResourcesDto,
  DiscordTemplateInstallInput,
  DiscordTemplateInstallResponse,
} from '@/Domain/api/Action/dto/DiscordTemplate.dto';
import { HandoffDto } from '@/Domain/api/Action/dto/Handoff.dto';
import { KnowledgeBaseActionDto } from '@/Domain/api/Action/dto/KnowledgeBaseAction.dto';
import {
  ConnectRemoteMcpDto,
  RemoteMcpConnectResult,
  RemoteMcpCreateDto,
  RemoteMcpDto,
  RemoteMcpOAuthStartDto,
  RemoteMcpOAuthStartResult,
  RemoteMcpUpdateDto,
} from '@/Domain/api/Action/dto/RemoteMCP.dto';
import { ShopifyMcpDto } from '@/Domain/api/Action/dto/ShopifyMcp.dto';
import { VisitorFeedbackAnalyticsDto } from '@/Domain/api/Analytics/dto/VisitorFeedbackAnalyticsDto';
import { ErrorResponse } from '@/Domain/api/App/dto/Error.dto';
import {
  AssistedRevenueAvailabilityDto,
  AssistedRevenueSummaryDto,
  RecentAssistedOrdersResponseDto,
} from '@/Domain/api/AssistedOrders/AssistedOrders.dto';
import { ChangelogApi } from '@/Domain/api/Changelog/Changelog.api';
import type {
  LinkedChannels,
  WhatsAppLinkOffer,
} from '@/Domain/api/ChannelIdentity/ChannelIdentity.api';
import {
  ConversationTopicsApi,
  TopicCreateInput,
} from '@/Domain/api/Conversation/ConversationTopics.api';
import { ConversationBadgeKind } from '@/Domain/api/Conversation/dto/ConversationBadge.dto';
import { MessageFeedbackInput } from '@/Domain/api/Conversation/dto/Message.dto';
import { TopicDto } from '@/Domain/api/Conversation/dto/Topic.dto';
import { ActivitySummaryPreferencesDto } from '@/Domain/api/EmailSummaries/ActivitySummaryPreferences.dto';
import { MarketingConsentDto } from '@/Domain/api/EmailSummaries/MarketingConsent.dto';
import {
  ConversationExportJobResponse,
  DataGatheringExportResponse,
  ExportDownloadPayload,
  UrlToFile,
} from '@/Domain/api/Export/export.dto';
import {
  assertExportFilterApplied,
  ConversationExportJobError,
  handleExportFailure,
  openDownloadUrl,
} from '@/Domain/api/Export/export.react-query';
import { ArticleDto } from '@/Domain/api/KnowledgeBase/dto/Article.dto';
import { ImportYoutubeDto } from '@/Domain/api/KnowledgeBase/dto/ImportUrl.dto';
import {
  SyncedSiteDto,
  SyncedSiteScopeDto,
  SyncedSiteSyncResultDto,
  RecentAdditionDto,
  MissingPageDto,
} from '@/Domain/api/KnowledgeBase/dto/SyncedSite.dto';
import { ShopifyApi } from '@/Domain/api/Shopify/Shopify.api';
import {
  CreditTopupAutoReload,
  CreditTopupAutoReloadInput,
  CreditTopupCardSetupInput,
  CreditTopupCheckoutInput,
  CreditTopupCheckoutUrl,
  CreditTopupOffer,
} from '@/Domain/api/Subscription/dto/CreditTopup.dto';
import { Usage } from '@/Domain/api/Subscription/dto/SubscriptionUsage.dto';
import { UserApi } from '@/Domain/api/User/User.api';
import type { UserEmailChangeInput } from '@/Domain/api/User/dto/User.dto';
import {
  ConversationRatingConfigDto,
  UpdateConversationRatingConfigDto,
} from '@/Domain/api/WIdget/dto/ConversationRatingConfig.dto';
import {
  SurveySettingsDto,
  UpdateSurveySettingsDto,
} from '@/Domain/api/WIdget/dto/SurveySettings.dto';
import { WidgetTranslationDto } from '@/Domain/api/WIdget/dto/WidgetTranslation.dto';
import { SendTemplateRequest } from '@/Domain/api/WhatsAppTemplates/WhatsAppTemplates.api';
import { HandoffForm } from '@/Domain/components/Action/HandoffAction/HandoffAction.sheet';
import { HttpRequestCreateFromTextInput } from '@/Domain/components/Action/HttpRequestActionFromText/CreateHttpRequestAction.modal';
import {
  Integrations,
  ArticlesList,
  ArticlesPaginated,
} from '@/Domain/components/KnowledgeBaseArticles/types';
import { InboxStatus, UserInboxStatus } from '@/Domain/components/SocketProvider';
import { CampaignForm } from '@/Domain/hooks/Action/CampaignAction/useCampaignActionForm';
import { ApiMapper } from '@/Domain/mapper/App/Api.mapper';
import { ArticlesFilter } from '@/UI/KnowledgeBase/KnowledgeManager/KnowledgeManager.dto';
import {
  SonyAdminDocumentDetailDto,
  SonyAdminDocumentsListDto,
  SonyDocumentsFilter,
  SonyObjectTypeEnum,
} from '@/UI/KnowledgeBase/SonyDocuments/SonyDocuments.dto';

const SONY_ADMIN_DETAIL_POLL_INTERVAL_MS = 4000;
export const CONVERSATION_EXPORT_POLL_INTERVAL_MS = 2000;
export const CONVERSATION_EXPORT_MAX_POLL_INTERVAL_MS = 15_000;
// How long the tab keeps watching. The backend may still be on a later retry when
// this expires, so giving up here reports "still preparing", never "failed".
export const CONVERSATION_EXPORT_POLL_TIMEOUT_MS = 2_400_000;

const waitForConversationExportPoll = async (delayMs: number): Promise<void> =>
  await new Promise((resolve) => window.setTimeout(resolve, delayMs));

// Surfaced 4xx are deterministic: the axios interceptor has already done its
// token-refresh retry for 401s, and 403/404 won't change on replay — retrying
// them only spams the API (role-gated endpoints turned this into a refresh storm).
export const shouldRetryQuery = (failureCount: number, error: unknown): boolean => {
  const status = (error as { response?: { status?: number } })?.response?.status;
  const isClientError = status !== undefined && status >= 400 && status < 500;
  return !isClientError && failureCount < 3;
};

const queryClient = new QueryClient();
queryClient.setDefaultOptions({
  queries: {
    staleTime: Infinity,
    gcTime: Infinity,
    retry: shouldRetryQuery,
  },
});
configureEntryOperationsQueryClient(queryClient);

queryClient.setMutationDefaults([QueryKey.user.emailChange], {
  mutationFn: async (form: UserEmailChangeInput) => await UserApi.requestEmailChange(form),
});

queryClient.setQueryDefaults([QueryKey.conversationTopics.list], {
  queryFn: async () => await ConversationTopicsApi.list(),
});

queryClient.setMutationDefaults([QueryKey.conversationTopics.create], {
  mutationFn: async (topic: TopicCreateInput) => await ConversationTopicsApi.create(topic),
});

queryClient.setMutationDefaults([QueryKey.conversationTopics.update], {
  mutationFn: async (topic: TopicDto) => await ConversationTopicsApi.update(topic),
});

queryClient.setMutationDefaults([QueryKey.conversationTopics.delete], {
  mutationFn: async (conversationTopicId: number) =>
    await ConversationTopicsApi.remove(conversationTopicId),
});

queryClient.setQueryDefaults([QueryKey.knowledgeBase.get], {
  queryFn: async () => await httpClient.get('/v1/api/knowledge_base/').then((res) => res.data),
});

queryClient.setQueryDefaults([QueryKey.changelog.list], {
  queryFn: async () => await ChangelogApi.list(),
  staleTime: 5 * 60 * 1000,
  gcTime: 30 * 60 * 1000,
  // A retry on a decorative cross-origin asset only doubles the reported failures.
  retry: false,
});

queryClient.setMutationDefaults([QueryKey.knowledgeBase.get], {
  mutationFn: async (data) =>
    await httpClient.patch('/v1/api/knowledge_base/', data).then((res) => res.data),
});

queryClient.setQueryDefaults([QueryKey.knowledgeBase.siteMapPreview.get], {
  queryFn: async () => {
    try {
      const { data } = await httpClient.get(Endpoint.knowledgeBase.siteMapPreview.get);
      return data;
    } catch (error) {
      // 404 = no map started for this scenario yet, a valid idle state.
      if (error instanceof AxiosError && error.response?.status === 404) {
        return {
          status: 'none',
          total_urls: 0,
          tree: {},
          selection: [],
          scrape_limit: null,
          articles_left: null,
          error_message: null,
        };
      }
      throw error;
    }
  },
  retry: false,
  // The modal grafts lazily-loaded children into this cache; a focus refetch would clobber them.
  refetchOnWindowFocus: false,
  // Poll while the async /map job is running; stop once it settles.
  refetchInterval: (query) =>
    (query.state.data as { status?: string } | undefined)?.status === 'mapping' ? 2000 : false,
});

queryClient.setMutationDefaults([QueryKey.knowledgeBase.siteMapPreview.start], {
  mutationFn: async (variables: { url: string }) =>
    await httpClient
      .post(Endpoint.knowledgeBase.siteMapPreview.start, variables)
      .then((res) => res.data),
  // Flip the cache to "mapping" at once so the dock can't resurface the previous
  // site's ready map before this one finishes (one map per implementation), and
  // drop any lazy-children pages cached for the previous map.
  onMutate: () => {
    queryClient.removeQueries({ queryKey: [QueryKey.knowledgeBase.siteMapPreview.children] });
    queryClient.setQueryData([QueryKey.knowledgeBase.siteMapPreview.get], {
      status: 'mapping',
      total_urls: 0,
      tree: {},
      selection: [],
      scrape_limit: null,
      articles_left: null,
      error_message: null,
    });
  },
});

queryClient.setMutationDefaults([QueryKey.knowledgeBase.siteMapPreview.selection], {
  mutationFn: async (variables: { selection: string[] }) =>
    await httpClient
      .patch(Endpoint.knowledgeBase.siteMapPreview.selection, variables)
      .then((res) => res.data),
});

queryClient.setMutationDefaults([QueryKey.knowledgeBase.siteMapPreview.scrape], {
  mutationFn: async (variables: { selection: string[] }) =>
    await httpClient
      .post(Endpoint.knowledgeBase.siteMapPreview.scrape, variables)
      .then((res) => res.data),
});

queryClient.setMutationDefaults([QueryKey.knowledgeBase.siteMapPreview.delete], {
  mutationFn: async () =>
    await httpClient.delete(Endpoint.knowledgeBase.siteMapPreview.delete).then((res) => res.data),
});

queryClient.setQueryDefaults([QueryKey.knowledgeBase.siteMapPreview.children], {
  queryFn: async ({ queryKey }) => {
    const [, params] = queryKey as [string, { path: string; offset: number; limit: number }];
    return httpClient
      .get(Endpoint.knowledgeBase.siteMapPreview.children, { params })
      .then((res) => res.data);
  },
});

queryClient.setQueryDefaults([QueryKey.knowledgeBase.articlesInfinite], {
  queryFn: async ({ queryKey }) => {
    const [, params] = queryKey as [
      typeof QueryKey.knowledgeBase.articlesInfinite,
      { offset: number; limit: number; filters: ArticlesFilter },
    ];

    const { offset, limit, filters } = params;
    const { data } = await httpClient.get<ArticlesList>(
      Endpoint.knowledgeBase.articles.listOrFilter,
      {
        params: { offset, limit, ...filters },
        paramsSerializer: {
          indexes: null,
        },
      },
    );

    return data;
  },
});

queryClient.setQueryDefaults([QueryKey.knowledgeBase.tags], {
  queryFn: async (data) =>
    await httpClient.get(Endpoint.knowledgeBase.tags, data).then((res) => res.data),
});

queryClient.setQueryDefaults([QueryKey.knowledgeBase.tagsSearch], {
  queryFn: async ({ queryKey, signal }) => {
    const [, q, limit] = queryKey as [string, string, number];
    return await httpClient
      .get<string[]>(Endpoint.knowledgeBase.tagsSearch, {
        params: { q, limit },
        signal,
      })
      .then((res) => res.data);
  },
});

queryClient.setQueryDefaults(['settings'], {
  queryFn: async () => await httpClient.get('/settings').then((res) => res.data),
});

/** Bootstrap (SPA initialization) */

queryClient.setQueryDefaults([QueryKey.app.bootstrap], {
  queryFn: async () => await httpClient.get(Endpoint.app.bootstrap).then((res) => res.data),
});

queryClient.setQueryDefaults([QueryKey.app.supportIdentityToken], {
  queryFn: async () =>
    await httpClient.get(Endpoint.app.supportIdentityToken).then((res) => res.data),
});

/** Subscription */

queryClient.setQueryDefaults(['subscription'], {
  queryFn: async () =>
    await httpClient.get('/v1/api/subscription/allowed_features').then((res) => res.data),
});

queryClient.setQueryDefaults(['subscription-active'], {
  queryFn: async () => await httpClient.get('/v1/api/subscription/').then((res) => res.data),
});

queryClient.setQueryDefaults(['subscriptions-plans'], {
  queryFn: async () => await httpClient.get('/v1/api/offer/').then((res) => res.data),
});

queryClient.setQueryDefaults([QueryKey.subscription.usage], {
  queryFn: async (): Promise<Usage> =>
    await httpClient.get(Endpoint.subscription.usage).then((res) => res.data),
});

queryClient.setQueryDefaults([QueryKey.subscription.creditTopup], {
  queryFn: async (): Promise<CreditTopupOffer> =>
    await httpClient.get(Endpoint.subscription.creditTopup).then((res) => res.data),
});

export const startCreditTopupCheckoutMutationFn = async (
  input: CreditTopupCheckoutInput,
): Promise<CreditTopupCheckoutUrl> =>
  await httpClient.post(Endpoint.subscription.creditTopupCheckout, input).then((res) => res.data);

queryClient.setMutationDefaults([QueryKey.subscription.creditTopupCheckout], {
  mutationFn: startCreditTopupCheckoutMutationFn,
});

queryClient.setMutationDefaults([QueryKey.subscription.creditTopupCardSetup], {
  mutationFn: async ({ entrypoint }: CreditTopupCardSetupInput): Promise<CreditTopupCheckoutUrl> =>
    await httpClient
      .post(Endpoint.subscription.creditTopupCardSetup, undefined, { params: { entrypoint } })
      .then((res) => res.data),
});

queryClient.setMutationDefaults([QueryKey.subscription.creditTopupAutoReload], {
  mutationFn: async ({ enabled }: CreditTopupAutoReloadInput): Promise<CreditTopupAutoReload> =>
    await httpClient
      .put(Endpoint.subscription.creditTopupAutoReload, { enabled })
      .then((res) => res.data),
  onMutate: async ({ enabled }: CreditTopupAutoReloadInput) => {
    await queryClient.cancelQueries({ queryKey: [QueryKey.subscription.creditTopup] });
    const previous = queryClient.getQueryData<CreditTopupOffer>([
      QueryKey.subscription.creditTopup,
    ]);
    if (previous) {
      queryClient.setQueryData<CreditTopupOffer>([QueryKey.subscription.creditTopup], {
        ...previous,
        auto_reload: { ...previous.auto_reload, enabled },
      });
    }
    return { previous };
  },
  onError: (_error: unknown, _input: CreditTopupAutoReloadInput, context: unknown) => {
    const previous = (context as { previous?: CreditTopupOffer } | undefined)?.previous;
    if (previous) {
      queryClient.setQueryData([QueryKey.subscription.creditTopup], previous);
    }
  },
  onSettled: () => queryClient.invalidateQueries({ queryKey: [QueryKey.subscription.creditTopup] }),
});

queryClient.setQueryDefaults(['invitations'], {
  queryFn: async () =>
    await httpClient.get('/v1/api/scenario_users/invites').then((res) => res.data),
});

queryClient.setQueryDefaults(['users'], {
  queryFn: async () => await httpClient.get('/v1/api/scenario_users').then((res) => res.data),
});

// Resolves to the refreshed member list; exported so tests reuse it verbatim.
export const transferOwnershipMutationFn = async (userId: string): Promise<unknown[]> =>
  await httpClient
    .post(Endpoint.ai.transferOwnership.replace(':userId', userId))
    .then((res) => res.data);
queryClient.setMutationDefaults([QueryKey.scenarioUsers.transferOwnership], {
  mutationFn: transferOwnershipMutationFn,
});

queryClient.setMutationDefaults(['knowledge_base'], {
  mutationFn: async (data) =>
    await httpClient.patch('/v1/api/knowledge_base/', data).then((res) => res.data),
});

queryClient.setMutationDefaults(['settings'], {
  mutationFn: async (data) => await httpClient.patch('/settings', data).then((res) => res.data),
});

queryClient.setQueryDefaults([QueryKey.aiModel.get], {
  queryFn: async () => await httpClient.get(Endpoint.ai.model).then((res) => res.data),
});

queryClient.setMutationDefaults([QueryKey.aiModel.patch], {
  mutationFn: async (data: { advanced_model: string | null }) =>
    await httpClient.patch(Endpoint.ai.model, data).then((res) => res.data),
});

// Exported so tests can register the same mutationFn and a refactor of the
// signature (e.g. an object arg) shows up in the test suite instead of
// passing silently with a hand-copied stub.
export const deleteImplementationMutationFn = async (scenarioId?: string): Promise<void> => {
  const config = scenarioId ? { headers: { 'scenario-id': scenarioId } } : undefined;
  await httpClient.delete(Endpoint.implementation.delete, config);
};
queryClient.setMutationDefaults([QueryKey.implementation.delete], {
  mutationFn: deleteImplementationMutationFn,
});

queryClient.setQueryDefaults([QueryKey.knowledgeBase.articles], {
  queryFn: async () =>
    await httpClient
      .get(Endpoint.knowledgeBase.articles.listOrFilter)
      .then((res) => (res.data.items !== undefined ? res.data.items : res.data)),
});

queryClient.setMutationDefaults(['articles'], {
  mutationFn: async (data) =>
    await httpClient.post('/v1/api/knowledge_base/articles/', data).then(({ data }) => data),
});

queryClient.setQueryDefaults(['article'], {
  queryFn: async ({ queryKey }) => {
    const [, id] = queryKey;
    return await httpClient.get(`/v1/api/knowledge_base/articles/${id}`).then((r) => r.data);
  },
});

queryClient.setMutationDefaults(['article'], {
  mutationFn: async ({ id, ...data }) =>
    await httpClient.patch(`/v1/api/knowledge_base/articles/${id}`, data).then((r) => r.data),
});

queryClient.setMutationDefaults(['article-delete'], {
  mutationFn: async (ids) =>
    await httpClient.delete('/v1/api/knowledge_base/articles/', {
      data: typeof ids === 'number' ? [ids] : ids,
    }),
});

/** Article Refresh */
export type ArticleRefreshResponseDto = {
  article_id: number;
  status: 'refreshing';
};

export type BulkArticleRefreshInputDto = {
  article_ids: number[];
};

queryClient.setMutationDefaults([QueryKey.knowledgeBase.articles.refresh], {
  mutationFn: async (articleId: number): Promise<ArticleRefreshResponseDto> =>
    await httpClient
      .post(generatePath(Endpoint.knowledgeBase.articles.refresh, { articleId }))
      .then(ApiMapper.toResponseData),
});

queryClient.setMutationDefaults([QueryKey.knowledgeBase.articles.refreshAll], {
  mutationFn: async (data?: BulkArticleRefreshInputDto) => {
    if (data) {
      await httpClient.post(Endpoint.knowledgeBase.articles.refreshUrlArticles, data);
      return;
    }
    await httpClient.post(Endpoint.knowledgeBase.articles.refreshUrlArticles);
  },
  onSuccess: (_data, variables) => {
    toast.success(
      variables?.article_ids?.length
        ? i18n.t('knowledge_base:article.toast.refresh_started')
        : i18n.t('knowledge_base:article.toast.refresh_all_started'),
    );
    void queryClient.invalidateQueries({
      queryKey: [QueryKey.knowledgeBase.articlesInfinite],
    });
  },
  onError: (_error: unknown) => {
    toast.error(i18n.t('knowledge_base:article.toast.refresh_failed'));
  },
});

queryClient.setMutationDefaults([QueryKey.knowledgeBase.retryFailedEmbeds], {
  mutationFn: async (): Promise<{ retried: number }> =>
    await httpClient.post(Endpoint.knowledgeBase.retryFailedEmbeds).then((res) => res.data),
  onSuccess: () => {
    toast.success(i18n.t('topbar:embed_retry_success'));
    void queryClient.invalidateQueries({ queryKey: ['knowledge_base'] });
    void queryClient.invalidateQueries({
      queryKey: [QueryKey.knowledgeBase.articlesInfinite],
    });
  },
  onError: () => {
    toast.error(i18n.t('topbar:embed_retry_error'));
  },
});

/** URL auto-refresh: plan availability (paid gate) + the per-article toggle. */
export type AutoRefreshAvailabilityDto = {
  available: boolean;
};

queryClient.setQueryDefaults([QueryKey.knowledgeBase.autoRefresh], {
  queryFn: async (): Promise<AutoRefreshAvailabilityDto> =>
    await httpClient.get(Endpoint.knowledgeBase.autoRefresh).then((res) => res.data),
});

export type ArticleAutoRefreshInputDto = {
  articleId: string;
  enabled: boolean;
};

type ArticleAutoRefreshContext = {
  previousLists: [readonly unknown[], ArticlesList | undefined][];
  previousArticle: ArticleDto | undefined;
};

queryClient.setMutationDefaults([QueryKey.knowledgeBase.articles.autoRefresh], {
  mutationFn: async ({ articleId, enabled }: ArticleAutoRefreshInputDto): Promise<ArticleDto> =>
    await httpClient
      .put(Endpoint.knowledgeBase.articles.autoRefresh.replace(':articleId', articleId), {
        enabled,
      })
      .then((res) => res.data),
  onMutate: async ({ articleId, enabled }: ArticleAutoRefreshInputDto) => {
    await queryClient.cancelQueries({ queryKey: [QueryKey.knowledgeBase.articlesInfinite] });
    // Optimistically flip the switch in every cached list page and in the
    // open article sheet so the toggle feels instant; rolled back on error.
    const previousLists = queryClient.getQueriesData<ArticlesList>({
      queryKey: [QueryKey.knowledgeBase.articlesInfinite],
    });
    queryClient.setQueriesData<ArticlesList>(
      { queryKey: [QueryKey.knowledgeBase.articlesInfinite] },
      (old) =>
        old?.items
          ? {
              ...old,
              items: old.items.map((item) =>
                String(item.id) === articleId ? { ...item, auto_refresh_enabled: enabled } : item,
              ),
            }
          : old,
    );
    const previousArticle = queryClient.getQueryData<ArticleDto>([
      QueryKey.knowledgeBase.article,
      articleId,
    ]);
    if (previousArticle) {
      queryClient.setQueryData<ArticleDto>([QueryKey.knowledgeBase.article, articleId], {
        ...previousArticle,
        auto_refresh_enabled: enabled,
      });
    }
    return { previousLists, previousArticle } satisfies ArticleAutoRefreshContext;
  },
  onSuccess: (_data: ArticleDto, { enabled }: ArticleAutoRefreshInputDto) => {
    toast.success(
      i18n.t(
        enabled
          ? 'knowledge_base:auto_refresh.toast.enabled'
          : 'knowledge_base:auto_refresh.toast.disabled',
      ),
    );
  },
  onError: (_error: unknown, { articleId }: ArticleAutoRefreshInputDto, context: unknown) => {
    const { previousLists, previousArticle } = (context ??
      {}) as Partial<ArticleAutoRefreshContext>;
    previousLists?.forEach(([key, data]) => queryClient.setQueryData(key, data));
    if (previousArticle) {
      queryClient.setQueryData([QueryKey.knowledgeBase.article, articleId], previousArticle);
    }
    toast.error(i18n.t('knowledge_base:auto_refresh.toast.failed'));
  },
  onSettled: (_data, _error, { articleId }: ArticleAutoRefreshInputDto) => {
    void queryClient.invalidateQueries({ queryKey: [QueryKey.knowledgeBase.articlesInfinite] });
    void queryClient.invalidateQueries({
      queryKey: [QueryKey.knowledgeBase.article, articleId],
    });
  },
});

export type ArticleBulkAutoRefreshInputDto = {
  article_ids: number[];
  enabled: boolean;
};

queryClient.setMutationDefaults([QueryKey.knowledgeBase.articles.autoRefreshBulk], {
  mutationFn: async ({ article_ids, enabled }: ArticleBulkAutoRefreshInputDto) =>
    await httpClient
      .put(Endpoint.knowledgeBase.articles.autoRefreshBulk, { article_ids, enabled })
      .then((res) => res.data),
  onSuccess: (_data, { enabled }: ArticleBulkAutoRefreshInputDto) => {
    toast.success(
      i18n.t(
        enabled
          ? 'knowledge_base:auto_refresh.toast.bulk_enabled'
          : 'knowledge_base:auto_refresh.toast.bulk_disabled',
      ),
    );
    void queryClient.invalidateQueries({ queryKey: [QueryKey.knowledgeBase.articlesInfinite] });
  },
  onError: () => {
    toast.error(i18n.t('knowledge_base:auto_refresh.toast.failed'));
  },
});

export type SyncedSiteToggleInputDto = {
  siteId: number;
  sync_enabled: boolean;
};

queryClient.setQueryDefaults([QueryKey.knowledgeBase.syncedSites.list], {
  queryFn: async (): Promise<SyncedSiteDto[]> =>
    await httpClient.get(Endpoint.knowledgeBase.syncedSites.list).then((res) => res.data),
});

queryClient.setMutationDefaults([QueryKey.knowledgeBase.syncedSites.update], {
  mutationFn: async ({ siteId, sync_enabled }: SyncedSiteToggleInputDto): Promise<SyncedSiteDto> =>
    await httpClient
      .patch(Endpoint.knowledgeBase.syncedSites.update.replace(':siteId', String(siteId)), {
        sync_enabled,
      })
      .then((res) => res.data),
  onSuccess: (_data: SyncedSiteDto, { sync_enabled }: SyncedSiteToggleInputDto) => {
    toast.success(
      i18n.t(
        sync_enabled
          ? 'knowledge_base:synced_sites.toast.enabled'
          : 'knowledge_base:synced_sites.toast.disabled',
      ),
    );
    void queryClient.invalidateQueries({
      queryKey: [QueryKey.knowledgeBase.syncedSites.list],
    });
  },
  onError: () => {
    toast.error(i18n.t('knowledge_base:synced_sites.toast.failed'));
  },
});

// Baseline (remap:false) and re-map (remap:true) are cached under distinct keys
// so a remap never evicts the restorable baseline. `failed`/`throttled` come
// back as HTTP 200 with a status field, so only a missing site is a query error.
queryClient.setQueryDefaults([QueryKey.knowledgeBase.syncedSites.scope], {
  queryFn: async ({ queryKey }) => {
    const [, params] = queryKey as [string, { siteId: number; remap: boolean }];
    const { data } = await httpClient.get(
      Endpoint.knowledgeBase.syncedSites.scope.replace(':siteId', String(params.siteId)),
      { params: { remap: params.remap } },
    );
    return data;
  },
  retry: false,
  refetchOnWindowFocus: false,
});

queryClient.setQueryDefaults([QueryKey.knowledgeBase.syncedSites.scopeChildren], {
  queryFn: async ({ queryKey }) => {
    const [, params] = queryKey as [
      string,
      { siteId: number; path: string; offset: number; limit: number },
    ];
    const { siteId, ...rest } = params;
    return httpClient
      .get(Endpoint.knowledgeBase.syncedSites.scopeChildren.replace(':siteId', String(siteId)), {
        params: rest,
      })
      .then((res) => res.data);
  },
});

queryClient.setMutationDefaults([QueryKey.knowledgeBase.syncedSites.scopeUpdate], {
  mutationFn: async ({
    siteId,
    selection,
  }: {
    siteId: number;
    selection: string[];
  }): Promise<SyncedSiteScopeDto> =>
    await httpClient
      .patch(Endpoint.knowledgeBase.syncedSites.scope.replace(':siteId', String(siteId)), {
        selection,
      })
      .then((res) => res.data),
});

queryClient.setQueryDefaults([QueryKey.knowledgeBase.syncedSites.recentAdditions], {
  queryFn: async ({ queryKey }): Promise<RecentAdditionDto[]> => {
    const [, params] = queryKey as [string, { siteId: number }];
    return httpClient
      .get(
        Endpoint.knowledgeBase.syncedSites.recentAdditions.replace(
          ':siteId',
          String(params.siteId),
        ),
      )
      .then((res) => res.data);
  },
});

queryClient.setQueryDefaults([QueryKey.knowledgeBase.syncedSites.missingPages], {
  queryFn: async ({ queryKey }): Promise<MissingPageDto[]> => {
    const [, params] = queryKey as [string, { siteId: number }];
    return httpClient
      .get(
        Endpoint.knowledgeBase.syncedSites.missingPages.replace(':siteId', String(params.siteId)),
      )
      .then((res) => res.data);
  },
});

queryClient.setMutationDefaults([QueryKey.knowledgeBase.syncedSites.sync], {
  mutationFn: async ({ siteId }: { siteId: number }): Promise<SyncedSiteSyncResultDto> =>
    await httpClient
      .post(Endpoint.knowledgeBase.syncedSites.sync.replace(':siteId', String(siteId)))
      .then((res) => res.data),
});

export type BulkAutoTagInputDto = {
  article_ids?: number[] | null;
};

export type BulkAutoTagResponseDto = {
  dispatched: number;
};

export type AutoTagArticleResponseDto = {
  tags: string[];
};

export type AutoTagArticleInput = {
  articleId: number;
  // Optional unsaved-edit overrides sent to the prompt. Used by the article
  // sheet's "Auto-tag" button so content the user just pasted gets tagged
  // instead of whatever's stale in the DB. Overrides are prompt-only.
  content?: string;
  title?: string;
  url?: string;
};

/** Write a finished tag list into every cached KB-list page holding that row, so
 *  the spinner can clear and the chips appear in the same commit. */
export const applyArticleTagsToCachedLists = (articleId: string, tags: string[]): void => {
  queryClient.setQueriesData<ArticlesList>(
    { queryKey: [QueryKey.knowledgeBase.articlesInfinite] },
    (old) =>
      // Identity-preserving when the row isn't on this page, so a scrape-driven
      // auto-tag burst doesn't re-render every mounted list.
      old?.items?.some((item) => String(item.id) === articleId)
        ? {
            ...old,
            items: old.items.map((item) =>
              String(item.id) === articleId ? { ...item, tags } : item,
            ),
          }
        : old,
  );
};

queryClient.setMutationDefaults([QueryKey.knowledgeBase.articles.autoTag], {
  mutationFn: async (input: AutoTagArticleInput): Promise<AutoTagArticleResponseDto> => {
    const body: Record<string, string> = {};
    if (input.content !== undefined) body.content = input.content;
    if (input.title !== undefined) body.title = input.title;
    if (input.url !== undefined) body.url = input.url;
    const response = await httpClient.post<AutoTagArticleResponseDto>(
      Endpoint.knowledgeBase.articles.autoTag.replace(':articleId', String(input.articleId)),
      body,
    );
    return response.data;
  },
  onSuccess: (data: AutoTagArticleResponseDto, input: AutoTagArticleInput) => {
    // Paint from the response rather than a list round-trip; also covers a tab
    // whose socket died and never sees AUTO_TAGGED.
    applyArticleTagsToCachedLists(String(input.articleId), data.tags);
    void queryClient.invalidateQueries({
      queryKey: [QueryKey.knowledgeBase.articlesInfinite],
    });
    toast.success(i18n.t('knowledge_base:article.toast.auto_tagged'));
  },
  onError: (error: AxiosError<ErrorResponse>) => {
    const message =
      error.response?.data?.errors?.root?.[0]?.message ||
      i18n.t('knowledge_base:article.toast.auto_tag_failed');
    toast.error(message);
  },
});

queryClient.setMutationDefaults([QueryKey.knowledgeBase.articles.autoTagBulk], {
  mutationFn: async (data?: BulkAutoTagInputDto): Promise<BulkAutoTagResponseDto> => {
    const body = data ?? {};
    const response = await httpClient.post(Endpoint.knowledgeBase.articles.autoTagBulk, body);
    return response.data as BulkAutoTagResponseDto;
  },
  onSuccess: (data) => {
    toast.success(
      i18n.t('knowledge_base:article.toast.auto_tag_bulk_started', {
        count: data?.dispatched ?? 0,
      }),
    );
  },
  onError: (error: AxiosError<ErrorResponse>) => {
    const message =
      error.response?.data?.errors?.root?.[0]?.message ||
      i18n.t('knowledge_base:article.toast.auto_tag_failed');
    toast.error(message);
  },
});

queryClient.setMutationDefaults(['articles-website'], {
  mutationFn: async (data) =>
    await httpClient
      .post('/v1/api/knowledge_base/import_external/website', data)
      .then(({ data }) => data),
});

queryClient.setMutationDefaults(['articles-pdf'], {
  mutationFn: async (data: { pdf_file: File }) => {
    const formData = new FormData();
    formData.append('pdf_file', data.pdf_file);
    return await httpClient
      .post('/v1/api/knowledge_base/file_upload_api/pdf', formData)
      .then(({ data }) => data);
  },
});

queryClient.setQueryDefaults([QueryKey.widgetSettings], {
  queryFn: async () =>
    await httpClient
      .get(Endpoint.widgetSettings)
      .then((res) => res.data)
      .then((data) => ({ bubble_icon_image_base64: null, avatar_image_base64: null, ...data })),
});

queryClient.setMutationDefaults([QueryKey.widgetSettings], {
  mutationFn: async (data) =>
    await httpClient
      .patch(Endpoint.widgetSettings, data)
      .then((res) => res.data)
      .then((data) => ({ bubble_icon_image_base64: null, avatar_image_base64: null, ...data })),
});

queryClient.setQueryDefaults([QueryKey.widgetTranslations.list], {
  queryFn: async (): Promise<WidgetTranslationDto[]> =>
    await httpClient.get(Endpoint.widgetTranslations).then((res) => res.data),
});

queryClient.setMutationDefaults([QueryKey.widgetTranslations.upsert], {
  mutationFn: async (data: WidgetTranslationDto): Promise<WidgetTranslationDto> =>
    await httpClient.put(Endpoint.widgetTranslations, data).then((res) => res.data),
});

queryClient.setMutationDefaults([QueryKey.widgetTranslations.delete], {
  mutationFn: async ({ language, country }: { language: string; country: string }): Promise<void> =>
    await httpClient
      .delete(Endpoint.widgetTranslations, { params: { language, country } })
      .then((res) => res.data),
});

queryClient.setQueryDefaults([QueryKey.emailSummaries.activitySummary.get], {
  queryFn: async (): Promise<ActivitySummaryPreferencesDto> =>
    await httpClient.get(Endpoint.emailSummaries.activitySummary).then((res) => res.data),
});

queryClient.setMutationDefaults([QueryKey.emailSummaries.activitySummary.patch], {
  mutationFn: async (
    patch: Partial<ActivitySummaryPreferencesDto>,
  ): Promise<ActivitySummaryPreferencesDto> =>
    await httpClient.patch(Endpoint.emailSummaries.activitySummary, patch).then((res) => res.data),
});

queryClient.setQueryDefaults([QueryKey.emailSummaries.marketingConsent.get], {
  queryFn: async (): Promise<MarketingConsentDto> =>
    await httpClient.get(Endpoint.emailSummaries.marketingConsent).then((res) => res.data),
});

queryClient.setMutationDefaults([QueryKey.emailSummaries.marketingConsent.patch], {
  mutationFn: async (patch: MarketingConsentDto): Promise<MarketingConsentDto> =>
    await httpClient.patch(Endpoint.emailSummaries.marketingConsent, patch).then((res) => res.data),
});

export type GenerateConversationStartersResponseDto = {
  conversation_starters: string[];
};

queryClient.setMutationDefaults([QueryKey.conversationStarters.generate], {
  mutationFn: async (): Promise<GenerateConversationStartersResponseDto> => {
    const response = await httpClient.post<GenerateConversationStartersResponseDto>(
      Endpoint.conversationStarters.generate,
    );
    return response.data;
  },
  onError: () => {
    toast.error(i18n.t('widget:conversation_starters.generate_error'));
  },
});

queryClient.setQueryDefaults([QueryKey.invites.mine], {
  queryFn: async () => await httpClient.get(Endpoint.invites.mine).then((res) => res.data),
  // Without this the app-wide `staleTime: Infinity` makes both consumers' refetchOnMount/refetchOnWindowFocus no-ops.
  staleTime: 0,
});

queryClient.setMutationDefaults([QueryKey.invites.accept], {
  mutationFn: async (token: string) =>
    await httpClient.post(Endpoint.invites.accept.replace(':token', token)).then((res) => res.data),
});

queryClient.setMutationDefaults([QueryKey.invites.ignore], {
  mutationFn: async () =>
    await httpClient
      .post<{
        scenario_id: string;
      }>(Endpoint.invites.ignore)
      .then((res) => res.data),
});

queryClient.setMutationDefaults([QueryKey.invites.decline], {
  mutationFn: async (token: string) =>
    await httpClient
      .delete(Endpoint.invites.destroy.replace(':token', token))
      .then((res) => res.data),
});

queryClient.setQueryDefaults([QueryKey.aiAssistants.get], {
  queryFn: async () => await httpClient.get(Endpoint.aiAssistants.get).then((res) => res.data),
});

queryClient.setMutationDefaults([QueryKey.aiAssistants.dismissReviewPrompt], {
  mutationFn: async (data) =>
    await httpClient.post(Endpoint.aiAssistants.dismissReviewPrompt, data).then((res) => res.data),
});

queryClient.setMutationDefaults([QueryKey.aiAssistants.clone], {
  mutationFn: async (data) =>
    await httpClient.post(Endpoint.aiAssistants.clone, data).then((res) => res.data),
});

queryClient.setQueryDefaults([QueryKey.aiAssistants.cloneStatus], {
  queryFn: async ({ queryKey }) =>
    await httpClient
      .get(Endpoint.aiAssistants.cloneStatus.replace(':jobId', String(queryKey[1])))
      .then((res) => res.data),
});

queryClient.setMutationDefaults(['accounts/logout'], {
  mutationFn: async () => await httpClient.post('/accounts/logout/'),
});

/** Insights -> AI Insights */
queryClient.setQueryDefaults([QueryKey.analytics.linkTracking], {
  queryFn: async ({ queryKey }) => {
    const paginationLimit = 10;

    const [, { start, end, pageIndex, sortKey, sortDesc }] = queryKey as [
      string,
      { start: string; end: string; pageIndex: number; sortKey?: string; sortDesc?: boolean },
    ];

    const params = {
      start_date: start,
      end_date: end,
      limit: paginationLimit,
      offset: pageIndex * paginationLimit,
      sort_key: sortKey,
      is_sort_descending: sortDesc,
    };

    return await httpClient
      .get(Endpoint.analytics.linkTracking, { params })
      .then(ApiMapper.toResponseData);
  },
  // Bound per-page staleness to the same duration as the server-side aggregate cache.
  staleTime: 300_000,
});

queryClient.setQueryDefaults([QueryKey.analytics.sourceUrls], {
  queryFn: async ({ queryKey }) => {
    const paginationLimit = 10;

    const [, { start, end, pageIndex }] = queryKey as [
      string,
      { start: string; end: string; pageIndex: number },
    ];

    const params = {
      start_date: start,
      end_date: end,
      limit: paginationLimit,
      offset: pageIndex * paginationLimit,
    };

    return await httpClient
      .get(Endpoint.analytics.sourceUrls, { params })
      .then(ApiMapper.toResponseData);
  },
});

queryClient.setQueryDefaults([QueryKey.analytics.general], {
  queryFn: async ({ queryKey }) => {
    const [, { start, end }] = queryKey as [string, { start: string; end: string }];
    return await httpClient
      .get(Endpoint.analytics.general, { params: { start_date: start, end_date: end } })
      .then((res) => res.data);
  },
});

/** Insights -> KB Sources */
queryClient.setQueryDefaults([QueryKey.analytics.kbSources], {
  queryFn: async ({ queryKey }) => {
    const paginationLimit = 10;

    const [, { start, end, pageIndex, sortKey, sortDesc }] = queryKey as [
      string,
      { start: string; end: string; pageIndex: number; sortKey: string; sortDesc: boolean },
    ];

    const params = {
      start_date: start,
      end_date: end,
      limit: paginationLimit,
      offset: pageIndex * paginationLimit,
      sort_key: sortKey,
      is_sort_descending: sortDesc,
    };

    return await httpClient
      .get(Endpoint.analytics.kbSources, { params })
      .then(ApiMapper.toResponseData);
  },
  // Overrides the app-wide `staleTime: Infinity`: the backend caches this window aggregate
  // for 300s, so without a matching client staleness the tab can hold one snapshot while a
  // later page request returns a newer one, making pagination internally inconsistent.
  staleTime: 300_000,
});

queryClient.setQueryDefaults([QueryKey.analytics.kbSourcesExamples], {
  queryFn: async ({ queryKey }) => {
    const [, { articleId, start, end }] = queryKey as [
      string,
      { articleId: number; start: string; end: string },
    ];

    return await httpClient
      .get(Endpoint.analytics.kbSourcesExamples.replace(':articleId', articleId.toString()), {
        params: { start_date: start, end_date: end },
      })
      .then(ApiMapper.toResponseData);
  },
  staleTime: 300_000,
});

queryClient.setQueryDefaults([QueryKey.analytics.userIntentDistribution], {
  queryFn: async ({ queryKey }) => {
    const [, { start, end }] = queryKey as [string, { start: string; end: string }];
    return await httpClient
      .get(Endpoint.analytics.userIntentDistribution, {
        params: { start_date: start, end_date: end },
      })
      .then((res) => res.data);
  },
});

queryClient.setQueryDefaults([QueryKey.analytics.externalCsat], {
  queryFn: async ({ queryKey }) => {
    const [, { start, end }] = queryKey as [string, { start: string; end: string }];
    return await httpClient
      .get(Endpoint.analytics.externalCsat, {
        params: { start_date: start, end_date: end },
      })
      .then((res) => res.data);
  },
});

queryClient.setQueryDefaults([QueryKey.analytics.ttfr], {
  queryFn: async ({ queryKey }) => {
    const [, { start, end }] = queryKey as [string, { start: string; end: string }];
    return await httpClient
      .get(Endpoint.analytics.ttfr, {
        params: { start_date: start, end_date: end },
      })
      .then((res) => res.data);
  },
});

queryClient.setQueryDefaults([QueryKey.analytics.unifiedInsightsList], {
  queryFn: async ({ queryKey, pageParam }) => {
    const [, { insightType, topic }] = queryKey as [
      string,
      { insightType: string; topic?: string | null },
    ];
    const params: Record<string, string | number> = {
      insight_type: insightType,
      limit: 20,
    };
    if (topic) {
      params.topic = topic;
    }
    if (pageParam) {
      params.cursor = pageParam;
    }
    return await httpClient
      .get(Endpoint.analytics.unifiedInsightsList, { params })
      .then((res) => res.data);
  },
});

type UnreadCount = {
  unread: number;
  capped: boolean;
  market_insights_unread: number;
  alerts_unread: number;
};

queryClient.setQueryDefaults([QueryKey.analytics.unreadInsightsCount], {
  queryFn: async () =>
    await httpClient
      .get(Endpoint.analytics.unreadInsightsCount)
      .then((res) => res.data as UnreadCount),
  // Background badge: 180s poll aligned with the backend cache TTL. The finite
  // staleTime overrides the app-wide Infinity so refocusing a stale tab refetches.
  refetchInterval: 180_000,
  staleTime: 180_000,
});

type InsightListPages = {
  pages: {
    items: {
      conversation_conv_id: string;
      market_insight_reviewed_at: string | null;
      market_insight: string | null;
      alert_match_id: number | null;
      alert_name: string | null;
      alert_reviewed_at: string | null;
    }[];
    has_more: boolean;
  }[];
  pageParams: unknown[];
};

type SetInsightReviewedVars = {
  conv_id: string;
  reviewed: boolean;
  alert_match_id?: number | null;
};

// Bump one slot of the split unread counter and recompute the sum. Used by
// both setInsightReviewed (delta=±1) and markAllInsightsReviewed (delta=
// negative-of-current-slot to zero it out).
const patchUnreadSlot = (
  old: UnreadCount | undefined,
  slot: 'market_insights_unread' | 'alerts_unread',
  delta: number,
): UnreadCount | undefined => {
  if (!old) return old;
  const next = Math.max(0, old[slot] + delta);
  const otherSlot =
    slot === 'market_insights_unread' ? old.alerts_unread : old.market_insights_unread;
  return {
    ...old,
    [slot]: next,
    unread: next + otherSlot,
    capped: delta < 0 ? false : old.capped,
  };
};

queryClient.setMutationDefaults([QueryKey.analytics.setInsightReviewed], {
  mutationFn: async ({ conv_id, reviewed, alert_match_id }: SetInsightReviewedVars) =>
    await httpClient
      // The endpoint requires exactly one target identifier: alert_match_id for an
      // alert row, conv_id for a market insight.
      .post(
        Endpoint.analytics.setInsightReviewed,
        alert_match_id != null ? { alert_match_id, reviewed } : { conv_id, reviewed },
      )
      .then((res) => res.data),
  onMutate: ({ conv_id, reviewed, alert_match_id }: SetInsightReviewedVars) => {
    const ts = reviewed ? new Date().toISOString() : null;
    const isAlert = alert_match_id != null;
    // Patch every active list page so the row dims/un-dims without round-tripping.
    queryClient.setQueriesData<InsightListPages>(
      { queryKey: [QueryKey.analytics.unifiedInsightsList] },
      (old) =>
        old
          ? {
              ...old,
              pages: old.pages.map((page) => ({
                ...page,
                items: page.items.map((i) => {
                  if (isAlert) {
                    return i.alert_match_id === alert_match_id
                      ? { ...i, alert_reviewed_at: ts }
                      : i;
                  }
                  if (i.conversation_conv_id !== conv_id) return i;
                  return { ...i, market_insight_reviewed_at: ts };
                }),
              })),
            }
          : old,
    );
    queryClient.setQueryData<UnreadCount>([QueryKey.analytics.unreadInsightsCount], (old) =>
      patchUnreadSlot(old, isAlert ? 'alerts_unread' : 'market_insights_unread', reviewed ? -1 : 1),
    );
  },
  onSettled: () => {
    void queryClient.invalidateQueries({
      queryKey: [QueryKey.analytics.unifiedInsightsList],
    });
    void queryClient.invalidateQueries({
      queryKey: [QueryKey.analytics.unreadInsightsCount],
    });
  },
  onError: () => {
    toast.error(i18n.t('insights_page:unified_ai_insights.toasts.review_failed'));
  },
});

type MarkAllInsightsReviewedVars = { scope: 'market_insights' | 'alerts' };

queryClient.setMutationDefaults([QueryKey.analytics.markAllInsightsReviewed], {
  mutationFn: async ({ scope }: MarkAllInsightsReviewedVars) =>
    await httpClient
      .post(Endpoint.analytics.markAllInsightsReviewed, undefined, { params: { scope } })
      .then((res) => res.data as { ok: boolean; count: number }),
  onMutate: ({ scope }: MarkAllInsightsReviewedVars) => {
    const now = new Date().toISOString();
    queryClient.setQueriesData<InsightListPages>(
      { queryKey: [QueryKey.analytics.unifiedInsightsList] },
      (old) =>
        old
          ? {
              ...old,
              pages: old.pages.map((page) => ({
                ...page,
                items: page.items.map((i) => {
                  if (scope === 'alerts') {
                    return i.alert_name && i.alert_reviewed_at === null
                      ? { ...i, alert_reviewed_at: now }
                      : i;
                  }
                  return i.market_insight && i.market_insight_reviewed_at === null
                    ? { ...i, market_insight_reviewed_at: now }
                    : i;
                }),
              })),
            }
          : old,
    );
    queryClient.setQueryData<UnreadCount>([QueryKey.analytics.unreadInsightsCount], (old) => {
      if (!old) return old;
      if (scope === 'alerts') {
        return { ...old, alerts_unread: 0, unread: old.market_insights_unread, capped: false };
      }
      return { ...old, market_insights_unread: 0, unread: old.alerts_unread, capped: false };
    });
  },
  onSettled: () => {
    void queryClient.invalidateQueries({
      queryKey: [QueryKey.analytics.unifiedInsightsList],
    });
    void queryClient.invalidateQueries({
      queryKey: [QueryKey.analytics.unreadInsightsCount],
    });
  },
  onError: () => {
    toast.error(i18n.t('insights_page:unified_ai_insights.toasts.review_failed'));
  },
});

/** Actions -> Campaigns */
queryClient.setQueryDefaults([QueryKey.campaign.list], {
  queryFn: async () =>
    await httpClient
      .get(Endpoint.campaigns.list)
      .then(ApiMapper.toResponseData)
      .catch(ApiMapper.toResponseError),
});

queryClient.setQueryDefaults([QueryKey.campaign.get], {
  queryFn: async ({ queryKey: [, campaignId] }: any) =>
    await httpClient
      .get(generatePath(Endpoint.campaigns.get, { campaignId }))
      .then(ApiMapper.toResponseData)
      .catch(ApiMapper.toResponseError),
});

queryClient.setMutationDefaults([QueryKey.campaign.update], {
  mutationFn: async ({ id, ...data }: CampaignForm) =>
    await httpClient
      .patch(generatePath(Endpoint.campaigns.update, { campaignId: id.toString() }), data)
      .then(ApiMapper.toResponseData)
      .catch(ApiMapper.toResponseError),
  onSuccess: (_, data) => {
    queryClient.setQueryData<CampaignDto>([QueryKey.campaign.get, data.id], data);
    if (data?.is_enabled) {
      toast.info(i18n.t('campaigns:toast.update_enabled'));
      return;
    }
    toast.info(i18n.t('campaigns:toast.update_disabled'));
  },
});

/** Actions -> Basic */
queryClient.setQueryDefaults([QueryKey.actions.basic.list], {
  queryFn: async () =>
    await httpClient.get(Endpoint.actions.basic.list).then(ApiMapper.toResponseData),
});

queryClient.setQueryDefaults([QueryKey.actions.basic.stats], {
  staleTime: 60_000,
  queryFn: async (): Promise<AIActionUsageStatsResponseDto> =>
    await httpClient.get(Endpoint.actions.basic.stats).then(ApiMapper.toResponseData),
});

/** Assisted Orders — AI-assisted ecommerce revenue attribution */
queryClient.setQueryDefaults([QueryKey.assistedOrders.summary], {
  staleTime: 60_000,
  queryFn: async ({ queryKey }): Promise<AssistedRevenueSummaryDto> => {
    const { start_date, end_date } = queryKey[1] as {
      start_date: string;
      end_date: string;
    };
    return await httpClient
      .get(Endpoint.assistedOrders.summary, { params: { start_date, end_date } })
      .then(ApiMapper.toResponseData);
  },
});

queryClient.setQueryDefaults([QueryKey.assistedOrders.recent], {
  staleTime: 60_000,
  queryFn: async ({ queryKey }): Promise<RecentAssistedOrdersResponseDto> => {
    const { start_date, end_date, limit, offset } = queryKey[1] as {
      start_date: string;
      end_date: string;
      limit: number;
      offset: number;
    };
    return await httpClient
      .get(Endpoint.assistedOrders.recent, {
        params: { start_date, end_date, limit, offset },
      })
      .then(ApiMapper.toResponseData);
  },
});

queryClient.setQueryDefaults([QueryKey.assistedOrders.availability], {
  staleTime: 5 * 60_000,
  queryFn: async (): Promise<AssistedRevenueAvailabilityDto> =>
    await httpClient.get(Endpoint.assistedOrders.availability).then(ApiMapper.toResponseData),
});

queryClient.setQueryDefaults([QueryKey.actions.basic.observed_metadata_keys], {
  staleTime: 5 * 60_000,
  refetchOnWindowFocus: false,
  queryFn: async (): Promise<ObservedMetadataKeyDto[]> =>
    await httpClient
      .get(Endpoint.actions.basic.observed_metadata_keys)
      .then(ApiMapper.toResponseData),
});

queryClient.setMutationDefaults([QueryKey.actions.basic.patch], {
  mutationFn: async ({ id, ...data }) =>
    await httpClient
      .patch(generatePath(Endpoint.actions.basic.patch, { actionId: id }), data)
      .then(ApiMapper.toResponseData)
      .catch(ApiMapper.toResponseError),
  onSuccess: (data) => {
    queryClient.setQueryData<ActionDto[]>([QueryKey.actions.basic.list], (old) => {
      if (!old) return [data];

      return old.map((item) => (item.id === data.id ? data : item));
    });
  },
});

queryClient.setMutationDefaults([QueryKey.actions.basic.delete], {
  mutationFn: async (actionId: number) => {
    await httpClient.delete(generatePath(Endpoint.actions.basic.delete, { actionId }));
    return actionId;
  },
  onSuccess: (_returned, actionId: number) => {
    queryClient.setQueryData([QueryKey.actions.basic.list], (old?: ActionDto[]) =>
      (old ?? []).filter((a) => a.id !== actionId),
    );
    toast.info(i18n.t('actions_page:toast.action_deleted'));
  },
});

/** Actions -> HttpRequest */
queryClient.setMutationDefaults([QueryKey.actions.httpRequest.create], {
  mutationFn: async (): Promise<HttpActionDto> =>
    httpClient
      .post<HttpActionDto>(Endpoint.actions.httpRequest.create)
      .then(ApiMapper.toResponseData),

  onSuccess: (data) => {
    toast.success(i18n.t('actions_page:toast.action_created'));
    queryClient.setQueryData<ActionDto[]>([QueryKey.actions.basic.list], (old) =>
      old ? [...old, data] : [data],
    );
  },
  onError: (error: AxiosError<ErrorResponse>) => {
    const message =
      error.response?.data?.errors?.root?.[0]?.message ||
      i18n.t('actions_page:toast.action_create_failed');
    toast.error(message);
  },
});

queryClient.setQueryDefaults([QueryKey.actions.httpRequest.get], {
  queryFn: async ({ queryKey }) =>
    await httpClient
      .get(generatePath(Endpoint.actions.httpRequest.get, { actionId: queryKey[1] }))
      .then(ApiMapper.toResponseData)
      .catch(ApiMapper.toResponseError),
});

queryClient.setMutationDefaults([QueryKey.actions.httpRequest.from_text], {
  mutationFn: async (data: HttpRequestCreateFromTextInput): Promise<HttpActionFromTextDto> =>
    await httpClient
      .post(Endpoint.actions.httpRequest.from_text, data)
      .then(ApiMapper.toResponseData),
  // No onSuccess - it is handled by appropriate return websocket emit
});

/** Actions -> Discord templates */
queryClient.setQueryDefaults([QueryKey.actions.discordTemplates.guilds], {
  staleTime: 0,
  queryFn: async (): Promise<DiscordTemplateGuildDto[]> =>
    await httpClient.get(Endpoint.actions.discordTemplates.guilds).then(ApiMapper.toResponseData),
});

queryClient.setQueryDefaults([QueryKey.actions.discordTemplates.resources], {
  staleTime: 0,
  queryFn: async ({ queryKey }): Promise<DiscordTemplateGuildResourcesDto> =>
    await httpClient
      .get(Endpoint.actions.discordTemplates.resources.replace(':guildId', String(queryKey[1])))
      .then(ApiMapper.toResponseData),
});

queryClient.setMutationDefaults([QueryKey.actions.discordTemplates.install], {
  mutationFn: async (data: DiscordTemplateInstallInput): Promise<DiscordTemplateInstallResponse> =>
    await httpClient
      .post(Endpoint.actions.discordTemplates.install, data)
      .then(ApiMapper.toResponseData),
  onSuccess: ({ actions }) => {
    queryClient.setQueryData<ActionDto[]>([QueryKey.actions.basic.list], (old) => {
      const installedIds = new Set(actions.map((action) => action.id));
      return [...(old ?? []).filter((action) => !installedIds.has(action.id)), ...actions];
    });
    toast.success(i18n.t('actions_page:discord_templates.install_success'));
  },
  onError: (error: AxiosError<ErrorResponse>) => {
    const message =
      error.response?.data?.errors?.root?.[0]?.message ||
      i18n.t('actions_page:discord_templates.install_error');
    toast.error(message);
  },
});

/** Actions -> Shopify MCP */

queryClient.setQueryDefaults([QueryKey.actions.shopifyMcp.get], {
  queryFn: async ({ queryKey }) =>
    await httpClient
      .get(generatePath(Endpoint.actions.shopifyMcp.get, { actionId: queryKey[1] }))
      .then(ApiMapper.toResponseData)
      .catch(ApiMapper.toResponseError),
});

queryClient.setMutationDefaults([QueryKey.actions.shopifyMcp.update], {
  mutationFn: async ({ id, ...data }: Partial<ShopifyMcpDto>) =>
    await httpClient
      .put(generatePath(Endpoint.actions.shopifyMcp.update, { actionId: id }), data)
      .then(ApiMapper.toResponseData)
      .catch(ApiMapper.toResponseError),
});

queryClient.setMutationDefaults([QueryKey.actions.shopifyMcp.test], {
  mutationFn: async ({ store_url }: { store_url: string }): Promise<string[]> =>
    await httpClient
      .post(Endpoint.actions.shopifyMcp.test, { store_url })
      .then(ApiMapper.toResponseData),
});

queryClient.setMutationDefaults([QueryKey.actions.shopifyMcp.create], {
  mutationFn: async (): Promise<ShopifyMcpDto> =>
    await httpClient.post(Endpoint.actions.shopifyMcp.create).then(ApiMapper.toResponseData),

  onSuccess: (data) => {
    toast.success(i18n.t('actions_page:toast.action_created'));
    queryClient.setQueryData<ActionDto[]>([QueryKey.actions.basic.list], (old) =>
      old ? [...old, data] : [data],
    );
  },
  onError: (error: AxiosError<ErrorResponse>) => {
    const message =
      error.response?.data?.errors?.root?.[0]?.message || i18n.t('shopify_mcp:toast.create_failed');
    toast.error(message);
  },
});

/** Actions -> Remote MCP */
queryClient.setMutationDefaults([QueryKey.actions.remoteMcp.connect], {
  mutationFn: async (data: ConnectRemoteMcpDto): Promise<RemoteMcpConnectResult> =>
    await httpClient
      .post(Endpoint.actions.remoteMcp.connect, data)
      .then(ApiMapper.toResponseData)
      .catch(ApiMapper.toResponseError),
  // No success toast: the probe persists nothing, and create/update already toast their own
  // outcome. It used to claim "Connected to the MCP server" the moment tools came back.
  onError: (error: AxiosError<ErrorResponse>) => {
    const message =
      error.response?.data?.errors?.root?.[0]?.message || i18n.t('remote_mcp:toast.connect_failed');
    toast.error(message);
  },
});

queryClient.setMutationDefaults([QueryKey.actions.remoteMcp.oauthStart], {
  mutationFn: async ({ action_id }: RemoteMcpOAuthStartDto): Promise<RemoteMcpOAuthStartResult> =>
    await httpClient
      .post(Endpoint.actions.remoteMcp.oauthStart.replace(':actionId', String(action_id)), {})
      .then(ApiMapper.toResponseData)
      .catch(ApiMapper.toResponseError),
  onError: () => {
    toast.error(i18n.t('remote_mcp:oauth.start_failed'));
  },
});

queryClient.setMutationDefaults([QueryKey.actions.remoteMcp.oauthDisconnect], {
  mutationFn: async ({ id }: { id: number }): Promise<RemoteMcpDto> =>
    await httpClient
      .post(Endpoint.actions.remoteMcp.oauthDisconnect.replace(':actionId', String(id)), {})
      .then(ApiMapper.toResponseData)
      .catch(ApiMapper.toResponseError),
  onSuccess: (data: RemoteMcpDto) => {
    queryClient.setQueryData([QueryKey.actions.remoteMcp.get, data.id], data);
    void queryClient.invalidateQueries({ queryKey: [QueryKey.actions.basic.list] });
  },
  onError: () => {
    toast.error(i18n.t('remote_mcp:oauth.disconnect_failed'));
  },
});

queryClient.setMutationDefaults([QueryKey.actions.remoteMcp.create], {
  mutationFn: async (data: RemoteMcpCreateDto) =>
    await httpClient
      .post(Endpoint.actions.remoteMcp.create, data)
      .then(ApiMapper.toResponseData)
      .catch(ApiMapper.toResponseError),
  onSuccess: (data) => {
    queryClient.setQueryData<ActionDto[]>([QueryKey.actions.basic.list], (old) =>
      old ? [...old, data] : [data],
    );
    toast.success(i18n.t('remote_mcp:toast.create_success'));
  },
  onError: (_error) => {
    toast.error(i18n.t('remote_mcp:toast.create_failed'));
  },
});

queryClient.setQueryDefaults([QueryKey.actions.remoteMcp.get], {
  queryFn: async ({ queryKey }) =>
    await httpClient
      .get(Endpoint.actions.remoteMcp.get.replace(':actionId', String(queryKey[1])))
      .then(ApiMapper.toResponseData)
      .catch(ApiMapper.toResponseError),
});

queryClient.setMutationDefaults([QueryKey.actions.remoteMcp.update], {
  mutationFn: async ({ id, ...data }: RemoteMcpUpdateDto) =>
    await httpClient
      .patch(Endpoint.actions.remoteMcp.update.replace(':actionId', String(id)), data)
      .then(ApiMapper.toResponseData)
      .catch(ApiMapper.toResponseError),
  onSuccess: (data) => {
    toast.success(i18n.t('remote_mcp:toast.update_success'));

    queryClient.setQueryData(
      [QueryKey.actions.remoteMcp.get, data.id],
      (_prevData: RemoteMcpDto) => data,
    );
    void queryClient.invalidateQueries({ queryKey: [QueryKey.actions.basic.list] });
  },
  onError: (_error) => {
    toast.error(i18n.t('remote_mcp:toast.update_failed'));
  },
});

/** Actions -> KnowledgeBase */
queryClient.setQueryDefaults([QueryKey.actions.knowledgeBase.get], {
  queryFn: async ({ queryKey }) =>
    await httpClient
      .get(generatePath(Endpoint.actions.knowledgeBase.get, { actionId: queryKey[1] }))
      .then(ApiMapper.toResponseData)
      .catch(ApiMapper.toResponseError),
});

queryClient.setMutationDefaults([QueryKey.actions.knowledgeBase.update], {
  mutationFn: async ({ id, ...data }: Partial<KnowledgeBaseActionDto>) =>
    await httpClient
      .put(generatePath(Endpoint.actions.knowledgeBase.update, { actionId: id }), data)
      .then(ApiMapper.toResponseData)
      .catch(ApiMapper.toResponseError),
});

queryClient.setMutationDefaults([QueryKey.actions.knowledgeBase.create], {
  mutationFn: async (): Promise<KnowledgeBaseActionDto> =>
    httpClient
      .post<HttpActionDto>(Endpoint.actions.knowledgeBase.create)
      .then(ApiMapper.toResponseData),

  onSuccess: (data) => {
    toast.success(i18n.t('actions_page:toast.action_created'));
    queryClient.setQueryData<ActionDto[]>([QueryKey.actions.basic.list], (old) =>
      old ? [...old, data] : [data],
    );
  },
});

/** Handoff */

queryClient.setQueryDefaults([QueryKey.handoff.get], {
  queryFn: async (): Promise<HandoffDto> =>
    await httpClient
      .get(Endpoint.handoff.get)
      .then(ApiMapper.toResponseData)
      .catch(ApiMapper.toResponseError),
});

queryClient.setMutationDefaults([QueryKey.handoff.put], {
  mutationFn: async (data: HandoffForm): Promise<HandoffDto> =>
    await httpClient
      .put(Endpoint.handoff.put, data)
      .then(ApiMapper.toResponseData)
      .catch(ApiMapper.toResponseError),
});

/** Export downloads */
queryClient.setQueryDefaults([QueryKey.export.conversationJob], {
  staleTime: 0,
  gcTime: 60_000,
  retry: shouldRetryQuery,
  queryFn: async ({ queryKey }): Promise<ConversationExportJobResponse> => {
    const [, jobId] = queryKey as [string, string];
    return await httpClient
      .get<ConversationExportJobResponse>(Endpoint.export.conversationJob.replace(':jobId', jobId))
      .then(ApiMapper.toResponseData)
      .catch(ApiMapper.toResponseError);
  },
});

queryClient.setMutationDefaults([QueryKey.export.conversations], {
  mutationFn: async (data: ExportDownloadPayload): Promise<UrlToFile> => {
    let job = (await httpClient
      .get(Endpoint.export.conversations, { params: data })
      .then(ApiMapper.toResponseData)
      .catch(ApiMapper.toResponseError)) as ConversationExportJobResponse;

    const giveUpAt = Date.now() + CONVERSATION_EXPORT_POLL_TIMEOUT_MS;
    let delayMs = CONVERSATION_EXPORT_POLL_INTERVAL_MS;
    while (job.status === 'pending' || job.status === 'running') {
      if (Date.now() >= giveUpAt) {
        throw new ConversationExportJobError('timed_out');
      }
      await waitForConversationExportPoll(delayMs);
      delayMs = Math.min(delayMs * 2, CONVERSATION_EXPORT_MAX_POLL_INTERVAL_MS);
      job = await queryClient.fetchQuery<ConversationExportJobResponse>({
        queryKey: [QueryKey.export.conversationJob, job.job_id],
        staleTime: 0,
      });
    }
    if (job.status === 'failed' || !job.url) {
      throw new ConversationExportJobError(job.error_code);
    }
    assertExportFilterApplied(data.metadata_key, job.metadata_key);
    return { url: job.url };
  },
  onSuccess: (data) => {
    queryClient.setQueryData([QueryKey.export.conversations], data);
    openDownloadUrl(data.url);
  },
  onError: (error) => handleExportFailure(error, 'Conversation export failed:'),
});

queryClient.setMutationDefaults([QueryKey.export.dataGathering], {
  mutationFn: async (data: ExportDownloadPayload): Promise<UrlToFile> => {
    const response = (await httpClient
      .get(Endpoint.export.dataGathering, { params: data })
      .then((res) => res.data)
      .catch(ApiMapper.toResponseError)) as DataGatheringExportResponse;
    assertExportFilterApplied(data.metadata_key, response.metadata_key);
    return { url: response.url };
  },
  onSuccess: (data) => {
    queryClient.setQueryData([QueryKey.export.dataGathering], data);
    openDownloadUrl(data.url);
  },
  onError: (error) => handleExportFailure(error, 'Data gathering export failed:'),
});

queryClient.setMutationDefaults([QueryKey.export.linkTracking], {
  mutationFn: async (data: ExportDownloadPayload): Promise<UrlToFile> =>
    await httpClient
      .get(Endpoint.export.linkTracking, { params: data })
      .then((res) => res.data)
      .catch(ApiMapper.toResponseError),
  onSuccess: (data) => {
    queryClient.setQueryData([QueryKey.export.linkTracking], data);
    openDownloadUrl(data.url);
  },
  onError: (error) => handleExportFailure(error, 'Link tracking export failed:'),
});

queryClient.setMutationDefaults([QueryKey.export.aiInsights], {
  mutationFn: async (data: ExportDownloadPayload): Promise<UrlToFile> =>
    await httpClient
      .get(Endpoint.export.aiInsights, { params: data })
      .then((res) => res.data)
      .catch(ApiMapper.toResponseError),
  onSuccess: (data) => {
    queryClient.setQueryData([QueryKey.export.aiInsights], data);
    openDownloadUrl(data.url);
  },
  onError: (error) => handleExportFailure(error, 'AI insights export failed:'),
});

queryClient.setMutationDefaults([QueryKey.export.triageData], {
  mutationFn: async (data: ExportDownloadPayload): Promise<UrlToFile> =>
    await httpClient
      .get(Endpoint.export.triageData, { params: data })
      .then((res) => res.data)
      .catch(ApiMapper.toResponseError),
  onSuccess: (data) => {
    queryClient.setQueryData([QueryKey.export.triageData], data);
    openDownloadUrl(data.url);
  },
  onError: (error) => handleExportFailure(error, 'Triage data export failed:'),
});

queryClient.setMutationDefaults([QueryKey.export.kbSources], {
  mutationFn: async (data: ExportDownloadPayload): Promise<UrlToFile> =>
    await httpClient
      .get(Endpoint.export.kbSources, { params: data })
      .then((res) => res.data)
      .catch(ApiMapper.toResponseError),
  onSuccess: (data) => {
    queryClient.setQueryData([QueryKey.export.kbSources], data);
    openDownloadUrl(data.url);
  },
  onError: (error) => handleExportFailure(error, 'KB sources export failed:'),
});

/** Shopify */
queryClient.setQueryDefaults([QueryKey.shopify.editorLink], {
  queryFn: async () => await ShopifyApi.getEditorLink().catch(() => ({ editor_url: null })),
});

queryClient.setMutationDefaults([QueryKey.shopify.startOauth], {
  mutationFn: async ({ shopUrl, scenarioId }: { shopUrl: string; scenarioId: string }) =>
    await ShopifyApi.startOauth(shopUrl, scenarioId),
});

/** Conersation Rating */
queryClient.setQueryDefaults([QueryKey.conversationRatingConfiguration.get], {
  queryFn: async (): Promise<ConversationRatingConfigDto> =>
    await httpClient
      .get(Endpoint.conversationRatingConfiguration.get)
      .then(ApiMapper.toResponseData),
});

queryClient.setMutationDefaults([QueryKey.conversationRatingConfiguration.patch], {
  mutationFn: async (
    data: UpdateConversationRatingConfigDto,
  ): Promise<ConversationRatingConfigDto> =>
    await httpClient.patch(Endpoint.conversationRatingConfiguration.patch, data),
  onSuccess: ({ data }) => {
    queryClient.setQueryData([QueryKey.conversationRatingConfiguration.get], data);
  },
});

queryClient.setQueryDefaults([QueryKey.conversationRatingConfiguration.surveySettings], {
  queryFn: async (): Promise<SurveySettingsDto> =>
    await httpClient
      .get(Endpoint.conversationRatingConfiguration.surveySettings)
      .then(ApiMapper.toResponseData),
});

queryClient.setMutationDefaults([QueryKey.conversationRatingConfiguration.surveySettingsPatch], {
  mutationFn: async (data: UpdateSurveySettingsDto): Promise<SurveySettingsDto> =>
    await httpClient
      .patch(Endpoint.conversationRatingConfiguration.surveySettingsPatch, data)
      .then(ApiMapper.toResponseData),
  onSuccess: (data: SurveySettingsDto) => {
    queryClient.setQueryData([QueryKey.conversationRatingConfiguration.surveySettings], data);
  },
});

queryClient.setQueryDefaults([QueryKey.analytics.conversationRating], {
  queryFn: async ({ queryKey }) => {
    const [, params] = queryKey as [
      typeof QueryKey.analytics.conversationRating,
      { start_date: string; end_date: string },
    ];

    const { start_date, end_date } = params;
    const { data } = await httpClient.get<VisitorFeedbackAnalyticsDto>(
      Endpoint.analytics.conversationRating,
      { params: { start_date, end_date } },
    );

    return data;
  },
});

/** Getting Started Progress (new onboarding) */
queryClient.setQueryDefaults([QueryKey.gettingStarted.progress], {
  // The DefaultLayout route guard blocks render while this query is loading,
  // so the default 3-retry backoff would hold the app on a blank screen for
  // ~13s on a backend outage. One retry keeps transient-blip resilience
  // without the long blank window; on permanent failure the guard falls
  // through to `!data → isComplete=true` and the user proceeds normally.
  retry: 1,
  // 404 means "no row exists for this implementation" — legacy ChatImplementations
  // never get one, and that's the signal to the frontend to skip the flow.
  // Swallow it to null here so consumers can branch on `data === null` instead
  // of wiring error plumbing.
  queryFn: async () => {
    try {
      const { data } = await httpClient.get(Endpoint.gettingStarted.progress);
      return data;
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 404) return null;
      throw error;
    }
  },
});

queryClient.setMutationDefaults([QueryKey.gettingStarted.progress], {
  mutationFn: async (data) =>
    await httpClient.patch(Endpoint.gettingStarted.progress, data).then((res) => res.data),
});

queryClient.setQueryDefaults([QueryKey.previewWebsite.get], {
  queryFn: async () => await httpClient.get(Endpoint.previewWebsite.get).then((res) => res.data),
});

queryClient.setMutationDefaults([QueryKey.previewWebsite.get], {
  mutationFn: async (data) =>
    await httpClient.patch(Endpoint.previewWebsite.get, data).then((res) => res.data),
  // Cancel again here (the hook already cancels in onMutate): a poll tick launched
  // DURING the mutation could otherwise land after this write and revive stale state.
  onSuccess: async (data) => {
    await queryClient.cancelQueries({ queryKey: [QueryKey.previewWebsite.get] });
    queryClient.setQueryData([QueryKey.previewWebsite.get], data);
  },
});

queryClient.setMutationDefaults([QueryKey.previewWebsite.refresh], {
  mutationFn: async () =>
    await httpClient.post(Endpoint.previewWebsite.refresh).then((res) => res.data),
  onSuccess: async (data) => {
    await queryClient.cancelQueries({ queryKey: [QueryKey.previewWebsite.get] });
    queryClient.setQueryData([QueryKey.previewWebsite.get], data);
  },
});

// Returns the saved onboarding discount or null if none has been issued (204
// from backend) or the saved one has expired. Frontend polls the live
// countdown locally off `expires_at`, so a stale cache for the timer is
// fine — only the existence flip needs to round-trip.
queryClient.setQueryDefaults([QueryKey.gettingStarted.discount], {
  retry: 1,
  queryFn: async () => {
    const response = await httpClient.get(Endpoint.gettingStarted.discount, {
      validateStatus: (status) => status === 200 || status === 204 || status === 404,
    });
    if (response.status === 200) return response.data;
    return null;
  },
});

queryClient.setMutationDefaults([QueryKey.gettingStarted.discount], {
  mutationFn: async () =>
    await httpClient.post(Endpoint.gettingStarted.discount).then((res) => res.data),
});

/** Onboarding Progress */
queryClient.setQueryDefaults([QueryKey.onboarding.progress], {
  queryFn: async () => await httpClient.get(Endpoint.onboarding.progress).then((res) => res.data),
});

queryClient.setMutationDefaults([QueryKey.onboarding.skip], {
  mutationFn: async (data: { step_id: string; skip: boolean }) =>
    await httpClient.post(Endpoint.onboarding.skip, data).then((res) => res.data),
});

queryClient.setMutationDefaults([QueryKey.onboarding.trackPageVisit], {
  mutationFn: async (data: { page: string }) =>
    await httpClient.post(Endpoint.onboarding.trackPageVisit, data).then((res) => res.data),
});

queryClient.setMutationDefaults([QueryKey.onboarding.fromUrl], {
  mutationFn: async (data: { url: string }) =>
    await httpClient.post(Endpoint.onboarding.fromUrl, data).then((res) => res.data),
});

queryClient.setMutationDefaults([QueryKey.onboarding.fromDescription], {
  mutationFn: async (data: { description: string; run_id: string }) =>
    await httpClient.post(Endpoint.onboarding.fromDescription, data).then((res) => res.data),
});

/** Conversation Sources (for GTM milestone events) */
queryClient.setQueryDefaults([QueryKey.conversationSources], {
  queryFn: async () => await httpClient.get(Endpoint.conversationSources).then((res) => res.data),
  staleTime: 5 * 60 * 1000, // 5 minutes — milestones are infrequent
});

/** WhatsApp Phone Numbers */
queryClient.setQueryDefaults([QueryKey.whatsappPhoneNumbers.status], {
  retry: false,
  queryFn: async () =>
    await httpClient.get(Endpoint.whatsappPhoneNumbers.status).then(ApiMapper.toResponseData),
});

queryClient.setQueryDefaults([QueryKey.whatsappPhoneNumbers.oauthState], {
  retry: false,
  queryFn: async ({ queryKey }) =>
    await httpClient
      .get(Endpoint.whatsappPhoneNumbers.oauthState, {
        params: { mode: queryKey[1], dialog: queryKey[2] },
      })
      .then(ApiMapper.toResponseData),
});

queryClient.setQueryDefaults([QueryKey.whatsappPhoneNumbers.list], {
  retry: false,
  queryFn: async () =>
    await httpClient.get(Endpoint.whatsappPhoneNumbers.list).then(ApiMapper.toResponseData),
});

queryClient.setMutationDefaults([QueryKey.whatsappPhoneNumbers.toggle], {
  mutationFn: async (data: { phone_number_id: string; is_active: boolean }) =>
    await httpClient
      .post(Endpoint.whatsappPhoneNumbers.toggle, data)
      .then(ApiMapper.toResponseData),
});

// Pre-built for upcoming test-message UI — not yet called from the frontend
queryClient.setMutationDefaults([QueryKey.whatsappPhoneNumbers.test], {
  mutationFn: async (data: { phone_number_id: string; recipient_number: string }) =>
    await httpClient.post(Endpoint.whatsappPhoneNumbers.test, data).then(ApiMapper.toResponseData),
});

queryClient.setMutationDefaults([QueryKey.whatsappPhoneNumbers.delete], {
  mutationFn: async () =>
    await httpClient.delete(Endpoint.whatsappPhoneNumbers.delete).then(ApiMapper.toResponseData),
});

/** WhatsApp Templates */
queryClient.setQueryDefaults([QueryKey.whatsappTemplates.list], {
  queryFn: async () =>
    await httpClient.get(Endpoint.whatsappTemplates.list).then(ApiMapper.toResponseData),
});

queryClient.setMutationDefaults([QueryKey.whatsappTemplates.send], {
  mutationFn: async ({
    conversationId,
    ...data
  }: { conversationId: string } & SendTemplateRequest) =>
    await httpClient.post(
      Endpoint.conversation.sendTemplate.replace(':conversationId', conversationId),
      data,
    ),
});

/** Message Feedback */
queryClient.setMutationDefaults([QueryKey.conversation.createMessageFeedback], {
  mutationFn: async ({
    conversationId,
    messageId,
    ...data
  }: { conversationId: string; messageId: string } & MessageFeedbackInput) =>
    await httpClient
      .post(
        Endpoint.conversation.messageFeedback
          .replace(':conversationId', conversationId)
          .replace(':messageId', messageId),
        data,
      )
      .then(ApiMapper.toResponseData),
});

queryClient.setMutationDefaults([QueryKey.conversation.updateMessageFeedback], {
  mutationFn: async ({
    conversationId,
    messageId,
    ...data
  }: { conversationId: string; messageId: string } & MessageFeedbackInput) =>
    await httpClient
      .patch(
        Endpoint.conversation.messageFeedback
          .replace(':conversationId', conversationId)
          .replace(':messageId', messageId),
        data,
      )
      .then(ApiMapper.toResponseData),
});

queryClient.setMutationDefaults([QueryKey.conversation.deleteMessageFeedback], {
  mutationFn: async ({
    conversationId,
    messageId,
  }: {
    conversationId: string;
    messageId: string;
  }) =>
    await httpClient.delete(
      Endpoint.conversation.messageFeedback
        .replace(':conversationId', conversationId)
        .replace(':messageId', messageId),
    ),
});

queryClient.setMutationDefaults([QueryKey.conversation.sendMedia], {
  mutationFn: async ({
    conversationId,
    file,
    caption,
  }: {
    conversationId: string;
    file: File;
    caption?: string;
  }) => {
    const formData = new FormData();
    formData.append('file', file);
    if (caption) formData.append('caption', caption);
    return await httpClient.post(
      Endpoint.conversation.sendMedia.replace(':conversationId', conversationId),
      formData,
    );
  },
});

export default queryClient;

/** Import endpoints */
queryClient.setMutationDefaults([QueryKey.knowledgeBase.import.youtubeVideo], {
  mutationFn: async (data: ImportYoutubeDto) =>
    await httpClient
      .post(Endpoint.knowledgeBase.import.youtubeVideo, data)
      .then(ApiMapper.toResponseData),
  // No onSuccess - it is handled by appropriate return websocket emit
  onError: (error, variables, _context) => {
    const status = error.response?.status;
    const errorToastMessage =
      status === 422
        ? i18n.t('knowledge_base:modals.upload_video.youtube.toast.invalid_url', {
            url: variables?.url,
          })
        : i18n.t('knowledge_base:modals.upload_video.youtube.toast.error', { url: variables?.url });
    toast.dismiss(variables.toast_process_uuid);
    toast.error(errorToastMessage, { id: variables.toast_process_uuid });
  },
});

/** Intercom */
queryClient.setMutationDefaults([QueryKey.knowledgeBase.intercom.import], {
  mutationFn: async () =>
    await httpClient.post(Endpoint.knowledgeBase.intercom.import).then(({ data }) => data),
  onSuccess: () => {
    toast.success(i18n.t('knowledge_base:modals.external_apps.intercom.success'));
    const currentIntegrations = queryClient.getQueryData<Integrations>(['integrations']);
    if (currentIntegrations) {
      currentIntegrations.intercom.has_articles = true;
      queryClient.setQueryData(['integrations'], currentIntegrations);
    }
    void queryClient.invalidateQueries({ queryKey: [QueryKey.knowledgeBase.articlesInfinite] });
  },
  onError: () => {
    toast.error(i18n.t('knowledge_base:modals.external_apps.intercom.error'));
  },
});

queryClient.setMutationDefaults([QueryKey.knowledgeBase.intercom.refresh], {
  mutationFn: async () => {
    // First, fetch all existing Intercom article IDs
    const queryParams = new URLSearchParams();
    queryParams.append('types', 'Intercom');
    queryParams.append('page_size', '10000'); // Large number to get all articles in one request

    const { data } = await httpClient.get<ArticlesPaginated>('/v1/api/knowledge_base/articles/', {
      params: queryParams,
    });

    // Extract article IDs and convert to integers (backend expects list[int])
    const articleIds = data.items.map((article) => parseInt(article.id, 10));

    // Call refresh endpoint with those IDs to delete and refetch all Intercom articles
    return await httpClient
      .post(Endpoint.knowledgeBase.intercom.refresh, {
        article_ids: articleIds,
        fetch_new_articles: true,
      })
      .then(({ data }) => data);
  },
  onSuccess: () => {
    toast.success(i18n.t('knowledge_base:modals.external_apps.intercom.success'));
    void queryClient.invalidateQueries({ queryKey: [QueryKey.knowledgeBase.articlesInfinite] });
  },
  onError: () => {
    toast.error(i18n.t('knowledge_base:modals.external_apps.intercom.error'));
  },
});

queryClient.setMutationDefaults([QueryKey.knowledgeBase.intercom.delete], {
  mutationFn: async () =>
    await httpClient.post(Endpoint.knowledgeBase.intercom.delete).then(({ data }) => data),
  onSuccess: () => {
    const currentIntegrations = queryClient.getQueryData<Integrations>(['integrations']);
    if (currentIntegrations) {
      currentIntegrations.intercom.has_articles = false;
      queryClient.setQueryData(['integrations'], currentIntegrations);
    }
    void queryClient.invalidateQueries({ queryKey: [QueryKey.knowledgeBase.articlesInfinite] });
  },
  onError: () => {
    toast.error(i18n.t('knowledge_base:modals.external_apps.intercom.error'));
  },
});

/** API Tokens */
queryClient.setQueryDefaults([QueryKey.apiTokens.list], {
  queryFn: async () => {
    const data = await httpClient.get(Endpoint.apiTokens.list).then(ApiMapper.toResponseData);
    // Sort tokens by created timestamp (newest first)
    return [...(data || [])].sort(
      (a: any, b: any) =>
        new Date(b.created_timestamp).getTime() - new Date(a.created_timestamp).getTime(),
    );
  },
});

queryClient.setQueryDefaults([QueryKey.apiTokens.legacyInfo], {
  queryFn: async () =>
    await httpClient.get(Endpoint.apiTokens.legacyInfo).then(ApiMapper.toResponseData),
});

queryClient.setMutationDefaults([QueryKey.apiTokens.create], {
  mutationFn: async ({ description, scopes }: { description: string; scopes: string[] }) =>
    await httpClient
      .post(Endpoint.apiTokens.create, { description, scopes })
      .then(ApiMapper.toResponseData),
});

queryClient.setMutationDefaults([QueryKey.apiTokens.revoke], {
  mutationFn: async (tokenUuid: string) =>
    await httpClient.post(Endpoint.apiTokens.revoke, { token_uuid: tokenUuid }),
});

/** Inbox */
queryClient.setQueryDefaults([QueryKey.inboxStatus.general], {
  queryFn: async (): Promise<InboxStatus> =>
    await httpClient.get(Endpoint.inboxStatus.general).then(({ data }) => data),
});

queryClient.setQueryDefaults([QueryKey.inboxStatus.my], {
  queryFn: async (): Promise<UserInboxStatus> =>
    await httpClient.get(Endpoint.inboxStatus.my).then(({ data }) => data),
});

/** Chat Page Access */
export type ChatPageAccess = 'public' | 'restricted' | 'disabled';

export interface ChatPageAccessDto {
  chat_page_access: ChatPageAccess;
}

queryClient.setQueryDefaults([QueryKey.chatPageAccess.get], {
  queryFn: async (): Promise<ChatPageAccessDto> =>
    await httpClient.get(Endpoint.chatPageAccess).then(({ data }) => data),
});

queryClient.setMutationDefaults([QueryKey.chatPageAccess.patch], {
  mutationFn: async (access: ChatPageAccess): Promise<ChatPageAccessDto> =>
    await httpClient
      .patch(Endpoint.chatPageAccess, { chat_page_access: access })
      .then(({ data }) => data),
  onSuccess: (data) => {
    queryClient.setQueryData([QueryKey.chatPageAccess.get], data);
  },
});

export interface SendInstallInstructionsData {
  emails: string[];
  note?: string;
}

export interface SendInstallInstructionsResponse {
  sent: number;
}

queryClient.setMutationDefaults([QueryKey.installInstructions.send], {
  mutationFn: async (data: SendInstallInstructionsData): Promise<SendInstallInstructionsResponse> =>
    await httpClient.post(Endpoint.installInstructions, data).then(({ data }) => data),
});

/** Inbox auto-close settings */
export interface InboxAutoCloseSettingsDto {
  auto_close_whatsapp: boolean;
  auto_close_other_channels: boolean;
}

queryClient.setQueryDefaults([QueryKey.inboxAutoCloseSettings.get], {
  queryFn: async (): Promise<InboxAutoCloseSettingsDto> =>
    await httpClient.get(Endpoint.inboxSettings.autoClose).then(({ data }) => data),
});

queryClient.setMutationDefaults([QueryKey.inboxAutoCloseSettings.patch], {
  mutationFn: async (
    payload: Partial<InboxAutoCloseSettingsDto>,
  ): Promise<InboxAutoCloseSettingsDto> =>
    await httpClient.patch(Endpoint.inboxSettings.autoClose, payload).then(({ data }) => data),
  onSuccess: (data) => {
    queryClient.setQueryData([QueryKey.inboxAutoCloseSettings.get], data);
  },
});

/** Inbox conversation badge settings */
export interface InboxConversationBadgeDto {
  // null = unconfigured default (topic in AI Insights, no badge in the Inbox), kept
  // distinct from an explicit 'topic' choice (which also shows the badge in the Inbox).
  badge_source: ConversationBadgeKind | null;
  badge_metadata_key: string | null;
}

queryClient.setQueryDefaults([QueryKey.inboxConversationBadge.get], {
  queryFn: async (): Promise<InboxConversationBadgeDto> =>
    await httpClient.get(Endpoint.inboxSettings.conversationBadge).then(({ data }) => data),
});

queryClient.setMutationDefaults([QueryKey.inboxConversationBadge.patch], {
  mutationFn: async (
    payload: Partial<InboxConversationBadgeDto>,
  ): Promise<InboxConversationBadgeDto> =>
    await httpClient
      .patch(Endpoint.inboxSettings.conversationBadge, payload)
      .then(({ data }) => data),
  onSuccess: (data) => {
    queryClient.setQueryData([QueryKey.inboxConversationBadge.get], data);
    // Refetch the surfaces that render the badge so the change shows without a manual reload.
    void queryClient.invalidateQueries({ queryKey: ['conversations'] });
    void queryClient.invalidateQueries({
      queryKey: [QueryKey.analytics.unifiedInsightsList],
    });
  },
});

/** Public URL */
export interface PublicUrlDto {
  public_url_active: boolean;
}

queryClient.setMutationDefaults([QueryKey.integration.publicUrl], {
  mutationFn: async (data: PublicUrlDto): Promise<PublicUrlDto> =>
    await httpClient.patch(Endpoint.publicUrl, data).then(({ data }) => data),
  onMutate: async (data: PublicUrlDto) => {
    await queryClient.cancelQueries({ queryKey: ['implementation'] });
    const previousData = queryClient.getQueryData(['implementation']);
    queryClient.setQueryData(['implementation'], (prevData: any) => ({
      ...prevData,
      ...data,
    }));
    return { previousData };
  },
  onError: (_error, _data, context) => {
    if (context?.previousData) {
      queryClient.setQueryData(['implementation'], context.previousData);
    }
  },
});

/** WhatsApp Templates */
queryClient.setMutationDefaults([QueryKey.whatsappTemplates.sendBulk], {
  mutationFn: async (data: unknown) =>
    await httpClient.post(Endpoint.whatsappTemplates.sendBulk, data).then(({ data }) => data),
});

queryClient.setMutationDefaults([QueryKey.whatsappTemplates.validatePhones], {
  mutationFn: async (data: unknown) =>
    await httpClient.post(Endpoint.whatsappTemplates.validatePhones, data).then(({ data }) => data),
});

/**
 * Channel integrations (Discord/Telegram/Slack/Hubspot/Intercom/Messenger/...)
 * use a uniform `[QueryKey.integration.get, name]` tuple so each integration
 * gets its own cache slot keyed by the integration name.
 */
queryClient.setQueryDefaults([QueryKey.integration.get], {
  queryFn: async ({ queryKey }) => {
    const [, name] = queryKey as [string, string];
    return await httpClient
      .get(Endpoint.integration.get.replace(':name', name))
      .then(({ data }) => data);
  },
});

// Generic PATCH for integrations — body shape is `{ name, params }` per the
// backend contract. Callers pass the same body via `mutate({ name, params })`.
queryClient.setMutationDefaults([QueryKey.integration.patch], {
  mutationFn: async ({ name, params }: { name: string; params: Record<string, unknown> }) =>
    await httpClient.patch(Endpoint.integration.patch, { name, params }).then(({ data }) => data),
});

/** Auto-close minutes (shared by Hubspot + Intercom) */
queryClient.setQueryDefaults([QueryKey.integration.autoCloseMinutes], {
  queryFn: async () =>
    await httpClient.get(Endpoint.integration.autoCloseMinutes).then(({ data }) => data),
});

queryClient.setMutationDefaults([QueryKey.integration.autoCloseMinutesPatch], {
  mutationFn: async (auto_close_minutes: number | null) =>
    await httpClient
      .patch(Endpoint.integration.autoCloseMinutes, { auto_close_minutes })
      .then(({ data }) => data),
});

/**
 * Zendesk — proxied through the Django ProxyView (`/api/integrations/...`)
 * to telegramquickchat. The endpoint takes `scenarioId` in the path; the query
 * key tuple is `[QueryKey.integration.zendeskGet, scenarioId]`.
 */
queryClient.setQueryDefaults([QueryKey.integration.zendeskGet], {
  // Fail fast like every other integration query: this GET proxies to the prod
  // telegram server, so the default 3-retry backoff turns a slow/failing hop
  // into a multi-second "Loading…" before the sheet falls back to its view.
  retry: false,
  queryFn: async ({ queryKey }) => {
    const [, scenarioId] = queryKey as [string, string];
    return await httpClient
      .get(Endpoint.integration.zendesk.replace(':scenarioId', scenarioId))
      .then(({ data }) => data);
  },
});

queryClient.setMutationDefaults([QueryKey.integration.zendeskCreate], {
  mutationFn: async ({ scenarioId, body }: { scenarioId: string; body: unknown }) =>
    await httpClient
      .post(Endpoint.integration.zendesk.replace(':scenarioId', scenarioId), body)
      .then(({ data }) => data),
});

queryClient.setMutationDefaults([QueryKey.integration.zendeskPatch], {
  mutationFn: async ({ scenarioId, body }: { scenarioId: string; body: unknown }) =>
    await httpClient
      .patch(Endpoint.integration.zendesk.replace(':scenarioId', scenarioId), body)
      .then(({ data }) => data),
});

queryClient.setMutationDefaults([QueryKey.integration.zendeskDelete], {
  mutationFn: async ({ scenarioId }: { scenarioId: string }) =>
    await httpClient
      .delete(Endpoint.integration.zendesk.replace(':scenarioId', scenarioId))
      .then(({ data }) => data),
});

/** Hubspot — pipelines + WhatsApp template */
queryClient.setQueryDefaults([QueryKey.integration.hubspotPipelines], {
  retry: false,
  queryFn: async () =>
    await httpClient.get(Endpoint.integration.hubspotPipelines).then(({ data }) => data),
});

queryClient.setMutationDefaults([QueryKey.integration.hubspotPipelinesPatch], {
  mutationFn: async (payload: {
    working_hours_pipeline_id: number;
    outside_working_hours_pipeline_id: number;
  }) =>
    await httpClient.patch(Endpoint.integration.hubspotPipelines, payload).then(({ data }) => data),
});

queryClient.setQueryDefaults([QueryKey.integration.hubspotWhatsappTemplate], {
  retry: false,
  queryFn: async () =>
    await httpClient.get(Endpoint.integration.hubspotWhatsappTemplate).then(({ data }) => data),
});

queryClient.setMutationDefaults([QueryKey.integration.hubspotWhatsappTemplatePatch], {
  mutationFn: async (payload: { template_name: string; template_language: string }) =>
    await httpClient
      .patch(Endpoint.integration.hubspotWhatsappTemplate, payload)
      .then(({ data }) => data),
});

queryClient.setQueryDefaults([QueryKey.integration.hubspotTokenStatus], {
  staleTime: 10 * 60 * 1000, // Cache for 10 minutes
  queryFn: async () =>
    await httpClient.get(Endpoint.integration.hubspotTokenStatus).then(({ data }) => data),
});

// Hubspot OAuth URL — fetched lazily on user click. Modeled as a mutation so
// that React Query exposes `mutateAsync()` and the caller can redirect the
// browser to the returned URL in the onSuccess callback. When an email is
// provided it rides in the OAuth state so the callback binds routing to it.
queryClient.setMutationDefaults([QueryKey.integration.hubspotOauthMutation], {
  mutationFn: async (variables?: { email?: string }): Promise<{ url: string }> => {
    const url = variables?.email
      ? `${Endpoint.integration.hubspotOauth}?email=${encodeURIComponent(variables.email)}`
      : Endpoint.integration.hubspotOauth;
    return await httpClient.get(url).then(({ data }) => data);
  },
});

/** Google Sheets — token-status (auto-created sheet) + lazy OAuth URL */
queryClient.setQueryDefaults([QueryKey.integration.googleSheetsStatus], {
  staleTime: 10 * 60 * 1000, // Cache for 10 minutes
  queryFn: async (): Promise<{ has_token: boolean; sheet_url: string }> =>
    await httpClient.get(Endpoint.integration.googleSheetsStatus).then(({ data }) => data),
});

queryClient.setMutationDefaults([QueryKey.integration.googleSheetsOauthMutation], {
  mutationFn: async (): Promise<{ url: string }> =>
    await httpClient.get(Endpoint.integration.googleSheetsOauth).then(({ data }) => data),
});

export interface DiscordTokenStatus {
  has_token: boolean;
  /** The own bot's enable toggle, so a coexistence conflict reads the same on both sheets. */
  bot_active: boolean;
}

/** Discord — token-status (gates the {{discord_bot_token}} AI Action System Token) */
queryClient.setQueryDefaults([QueryKey.integration.discordTokenStatus], {
  staleTime: 10 * 60 * 1000, // Cache for 10 minutes
  queryFn: async (): Promise<DiscordTokenStatus> =>
    await httpClient.get(Endpoint.integration.discordTokenStatus).then(({ data }) => data),
});

/**
 * Health of an own-bot deployment, most severe first. `error` defers to the gateway error
 * alert; `unconfirmed` means the token checks out but the gateway has never confirmed the
 * bot started, which `discord_bot_active` alone cannot tell you (it is the enable toggle).
 */
export type DiscordBotHealth =
  | 'error'
  | 'token_invalid'
  | 'unknown'
  | 'intent_off'
  | 'disabled'
  | 'unconfirmed'
  | 'live';

export interface DiscordBotStatus {
  name: string;
  username: string;
  health: DiscordBotHealth;
  /** Last gateway handshake, not a last-seen stamp; null means it never confirmed this bot. */
  gateway_confirmed_at: string | null;
  checked_at: string | null;
}

// Live bot profile + health fetched via getMe on the backend each time the integration
// sheet opens, so it always reflects the current name even after a rename, and the status
// line reports something we just verified rather than a stored flag that can be years old.
// staleTime 0 = refetch on every open; the backend caches the Discord round-trip for 60s.
queryClient.setQueryDefaults([QueryKey.integration.discordBotInfo], {
  staleTime: 0,
  queryFn: async (): Promise<DiscordBotStatus> =>
    await httpClient.get(Endpoint.integration.discordBotInfo).then(({ data }) => data),
});

export interface DiscordGuildHealth {
  guild_id: string;
  /** null when Discord could not be reached, so the row says "unknown" instead of "gone". */
  present: boolean | null;
}

// Nothing reconciles a missed GUILD_DELETE, so an ACTIVE guild row can outlive the shared
// bot's membership indefinitely; this asks Discord directly. staleTime 0 for the same
// reason as the guild list itself — a bot can be kicked out-of-band at any time.
queryClient.setQueryDefaults([QueryKey.integration.discordGuildsHealth], {
  staleTime: 0,
  queryFn: async (): Promise<{ guilds: DiscordGuildHealth[]; checked_at: string }> =>
    await httpClient.get(Endpoint.integration.discordGuildsHealth).then(({ data }) => data),
});

// Pre-save check of a pasted bot token via getMe (paste-time feedback in the
// connect dialog); nothing is persisted. 'unknown' = Discord unreachable, so
// callers fail open and let the regular connect flow proceed.
queryClient.setMutationDefaults([QueryKey.integration.discordValidateToken], {
  mutationFn: async ({
    token,
  }: {
    token: string;
  }): Promise<{
    status: 'valid' | 'invalid' | 'unknown';
    name: string;
    username: string;
    message_content_intent?: 'on' | 'off' | 'unknown';
  }> =>
    await httpClient
      .post(Endpoint.integration.discordValidateToken, { token })
      .then(({ data }) => data),
});

// One-click Discord (canonical "Quickchat AI" app): lazily-fetched authorize URL
// (redirect target), the list of connected servers, and per-guild enable
// PATCH + remove. The legacy own-bot token flow lives in the Advanced sheet.
export interface DiscordGuildSummary {
  guild_id: string;
  guild_name: string;
  enabled: boolean;
  state: 'PENDING' | 'ACTIVE' | 'REMOVED';
  /** The bot's display name in this server; empty means it shows as "Quickchat AI". */
  nickname?: string;
  /** What to prefill the rename field with when there is no nickname yet. */
  suggested_nickname?: string;
  added_at: string | null;
  connected_at?: string | null;
  automated_messages?: DiscordAutomatedMessages;
}

export interface DiscordAutomatedMessageRule {
  enabled: boolean;
  delay_seconds: number;
  message: string;
}

export interface DiscordAutomatedMessages {
  channel_created: DiscordAutomatedMessageRule & { category_id: string };
  member_joined: DiscordAutomatedMessageRule & { channel_id: string };
  thread_created: DiscordAutomatedMessageRule & { parent_channel_id: string };
}

export interface DiscordAutomationOption {
  id: string;
  name: string;
  /** False only when Discord answered and said no; an unreachable Discord leaves this true. */
  can_send: boolean;
}

export interface DiscordAutomatedMessageOptions {
  categories: DiscordAutomationOption[];
  channels: DiscordAutomationOption[];
  thread_parent_channels: DiscordAutomationOption[];
}

queryClient.setMutationDefaults([QueryKey.integration.discordOauth], {
  mutationFn: async ({ nickname }: { nickname: string }): Promise<{ url: string }> =>
    await httpClient
      .get(Endpoint.integration.discordOauth, { params: { nickname } })
      .then(({ data }) => data),
});

queryClient.setQueryDefaults([QueryKey.integration.discordGuilds], {
  queryFn: async (): Promise<DiscordGuildSummary[]> =>
    await httpClient.get(Endpoint.integration.discordGuilds).then(({ data }) => data),
  // a guild can leave out-of-band (bot kicked -> GUILD_DELETE), which nothing in-tab invalidates
  staleTime: 0,
});

queryClient.setMutationDefaults([QueryKey.integration.discordGuildPatch], {
  mutationFn: async ({
    guildId,
    ...body
  }: {
    guildId: string;
    enabled?: boolean;
    nickname?: string;
  }): Promise<DiscordGuildSummary> =>
    await httpClient
      .patch(Endpoint.integration.discordGuild.replace(':guild_id', guildId), body)
      .then(({ data }) => data),
});

queryClient.setMutationDefaults([QueryKey.integration.discordGuildDelete], {
  mutationFn: async ({ guildId }: { guildId: string }): Promise<void> => {
    await httpClient.delete(Endpoint.integration.discordGuild.replace(':guild_id', guildId));
  },
});

queryClient.setQueryDefaults([QueryKey.integration.discordAutomatedMessageOptions], {
  queryFn: async ({ queryKey }): Promise<DiscordAutomatedMessageOptions> =>
    await httpClient
      .get(
        Endpoint.integration.discordAutomatedMessageOptions.replace(
          ':guild_id',
          String(queryKey[1]),
        ),
      )
      .then(({ data }) => data),
  staleTime: 0,
});

queryClient.setMutationDefaults([QueryKey.integration.discordAutomatedMessagesUpdate], {
  mutationFn: async ({
    guildId,
    automatedMessages,
    rule,
  }: {
    guildId: string;
    automatedMessages: DiscordAutomatedMessages;
    rule: keyof DiscordAutomatedMessages;
  }): Promise<DiscordAutomatedMessages> =>
    await httpClient
      .put(Endpoint.integration.discordAutomatedMessages.replace(':guild_id', guildId), {
        ...automatedMessages,
        rule,
      })
      .then(({ data }) => data),
});

export interface DiscordAdvancedGuildSummary {
  guild_id: string;
  guild_name: string;
  automated_messages: DiscordAutomatedMessages;
}

export interface DiscordAdvancedGuildList {
  guilds: DiscordAdvancedGuildSummary[];
  server_members_intent: 'on' | 'off' | 'unknown';
}

queryClient.setQueryDefaults([QueryKey.integration.discordAdvancedGuilds], {
  queryFn: async (): Promise<DiscordAdvancedGuildList> =>
    await httpClient.get(Endpoint.integration.discordAdvancedGuilds).then(({ data }) => data),
  staleTime: 0,
});

queryClient.setQueryDefaults([QueryKey.integration.discordAdvancedAutomatedMessageOptions], {
  queryFn: async ({ queryKey }): Promise<DiscordAutomatedMessageOptions> =>
    await httpClient
      .get(
        Endpoint.integration.discordAdvancedAutomatedMessageOptions.replace(
          ':guild_id',
          String(queryKey[1]),
        ),
      )
      .then(({ data }) => data),
  staleTime: 0,
});

queryClient.setMutationDefaults([QueryKey.integration.discordAdvancedAutomatedMessagesUpdate], {
  mutationFn: async ({
    guildId,
    automatedMessages,
    rule,
  }: {
    guildId: string;
    automatedMessages: DiscordAutomatedMessages;
    rule: keyof DiscordAutomatedMessages;
  }): Promise<DiscordAutomatedMessages> =>
    await httpClient
      .put(Endpoint.integration.discordAdvancedAutomatedMessages.replace(':guild_id', guildId), {
        ...automatedMessages,
        rule,
      })
      .then(({ data }) => data),
});

// A one-click Telegram bind stays silent in PENDING until the user confirms it.
export type TelegramChatState = 'PENDING' | 'ACTIVE' | 'REMOVED';
export type TelegramChatKind = 'private' | 'group' | 'supergroup' | 'channel';

export interface TelegramChatSummary {
  chat_id: string;
  title: string;
  chat_type: TelegramChatKind;
  state: TelegramChatState;
  enabled: boolean;
  bot_is_admin: boolean;
  member_count: number | null;
  connected_by_username: string;
  added_at: string;
}

export interface TelegramConnectLink {
  url: string;
  nonce: string;
}

export interface TelegramOneClickAvailability {
  available: boolean;
  // A null management hint means its independent database probe failed.
  direct_link_active: boolean | null;
  has_connected_chats: boolean | null;
}

export interface TelegramDirectLink {
  url: string;
  active: boolean;
}

queryClient.setQueryDefaults([QueryKey.integration.telegramOneClickAvailability], {
  queryFn: async (): Promise<TelegramOneClickAvailability> =>
    await httpClient
      .get(Endpoint.integration.telegramOneClickAvailability)
      .then(({ data }) => data),
  // readiness is revoked out-of-band when the shared bot's webhook drifts, so never serve it stale
  staleTime: 0,
});

queryClient.setMutationDefaults([QueryKey.integration.telegramConnectLink], {
  mutationFn: async (): Promise<TelegramConnectLink> =>
    await httpClient.post(Endpoint.integration.telegramConnectLink).then(({ data }) => data),
});

queryClient.setQueryDefaults([QueryKey.integration.telegramChats], {
  queryFn: async (): Promise<TelegramChatSummary[]> =>
    await httpClient.get(Endpoint.integration.telegramChats).then(({ data }) => data),
  // a chat appears (bind) or leaves (bot kicked) out-of-band, so never serve it stale
  staleTime: 0,
});

queryClient.setMutationDefaults([QueryKey.integration.telegramChatConfirm], {
  mutationFn: async ({ chatId }: { chatId: string }): Promise<TelegramChatSummary> =>
    await httpClient
      .post(Endpoint.integration.telegramChatConfirm.replace(':chat_id', chatId))
      .then(({ data }) => data),
});

queryClient.setMutationDefaults([QueryKey.integration.telegramChatPatch], {
  // enabled is required, not optional: TelegramChatUpdate has no default, so {} is a 422.
  mutationFn: async ({
    chatId,
    ...body
  }: {
    chatId: string;
    enabled: boolean;
  }): Promise<TelegramChatSummary> =>
    await httpClient
      .patch(Endpoint.integration.telegramChat.replace(':chat_id', chatId), body)
      .then(({ data }) => data),
});

queryClient.setMutationDefaults([QueryKey.integration.telegramChatDelete], {
  mutationFn: async ({ chatId }: { chatId: string }): Promise<void> => {
    await httpClient.delete(Endpoint.integration.telegramChat.replace(':chat_id', chatId));
  },
});

queryClient.setQueryDefaults([QueryKey.integration.telegramDirectLink], {
  queryFn: async (): Promise<TelegramDirectLink> =>
    await httpClient.get(Endpoint.integration.telegramDirectLink).then(({ data }) => data),
  // another editor can flip this, and the app-wide staleTime is Infinity
  staleTime: 0,
});

queryClient.setMutationDefaults([QueryKey.integration.telegramDirectLinkPatch], {
  mutationFn: async ({ active }: { active: boolean }): Promise<TelegramDirectLink> =>
    await httpClient
      .patch(Endpoint.integration.telegramDirectLink, { active })
      .then(({ data }) => data),
});

queryClient.setQueryDefaults([QueryKey.integration.telegramBotInfo], {
  staleTime: 0,
  queryFn: async (): Promise<{ name: string; username: string }> =>
    await httpClient.get(Endpoint.integration.telegramBotInfo).then(({ data }) => data),
});

/** Intercom — validate-token + remove */
queryClient.setMutationDefaults([QueryKey.integration.intercomValidateToken], {
  mutationFn: async () =>
    await httpClient.post(Endpoint.integration.intercomValidateToken).then(({ data }) => data),
});

queryClient.setMutationDefaults([QueryKey.integration.intercomRemove], {
  mutationFn: async () =>
    await httpClient.delete(Endpoint.integration.intercomRemove).then(({ data }) => data),
});

/** Email — connect, reply address, verify domain, settings, remove */
export type EmailSettingsUpdateDto = {
  display_name?: string;
  signature?: string;
  rating_footer_active?: boolean;
  archive_copy_active?: boolean;
  reply_delay_minutes?: number;
};

queryClient.setMutationDefaults([QueryKey.integration.emailConnect], {
  mutationFn: async () =>
    await httpClient.post(Endpoint.integration.emailConnect).then(({ data }) => data),
});

queryClient.setMutationDefaults([QueryKey.integration.emailReplyAddress], {
  mutationFn: async (body: { reply_from_address: string; display_name?: string }) =>
    await httpClient.post(Endpoint.integration.emailReplyAddress, body).then(({ data }) => data),
});

queryClient.setMutationDefaults([QueryKey.integration.emailVerifyDomain], {
  mutationFn: async () =>
    await httpClient.post(Endpoint.integration.emailVerifyDomain).then(({ data }) => data),
});

queryClient.setMutationDefaults([QueryKey.integration.emailSettings], {
  mutationFn: async (body: EmailSettingsUpdateDto) =>
    await httpClient.post(Endpoint.integration.emailSettings, body).then(({ data }) => data),
});

queryClient.setMutationDefaults([QueryKey.integration.emailRemove], {
  mutationFn: async () =>
    await httpClient.delete(Endpoint.integration.emailRemove).then(({ data }) => data),
});

/** Messenger — disconnect (Facebook Messenger) */
queryClient.setMutationDefaults([QueryKey.integration.facebookMessengerDelete], {
  mutationFn: async () => {
    await httpClient.delete(Endpoint.integration.facebookMessengerDelete);
  },
});

export interface MessengerPageSummary {
  page_id: string;
  page_name: string;
  staged_at: string | null;
  is_connected: boolean;
}

export interface MessengerOAuthStateResponse {
  state: string;
}

queryClient.setQueryDefaults([QueryKey.integration.messengerOauthState], {
  retry: false,
  queryFn: async (): Promise<MessengerOAuthStateResponse> =>
    await httpClient.get(Endpoint.integration.messengerOauthState).then(({ data }) => data),
  staleTime: 5 * 60 * 1000,
  refetchInterval: 5 * 60 * 1000,
});

/** Messenger — Pages discovered during OAuth, awaiting an explicit pick */
queryClient.setQueryDefaults([QueryKey.integration.messengerPages], {
  queryFn: async (): Promise<MessengerPageSummary[]> =>
    await httpClient.get(Endpoint.integration.messengerPages).then(({ data }) => data),
  // the list is written by the OAuth popup, out of band from this tab
  staleTime: 0,
});

queryClient.setMutationDefaults([QueryKey.integration.messengerPageSelect], {
  mutationFn: async (pageId: string) => {
    await httpClient.post(Endpoint.integration.messengerPageSelect, { page_id: pageId });
  },
});

export interface InstagramOauthState {
  state: string;
  authorize_url: string;
}

/** Instagram — signed OAuth state + the dialog URL built server-side.
 * The mode is baked into the signed state, so a popup state cannot drive a
 * full-page redirect: the two launch modes need two separately issued states. */
queryClient.setQueryDefaults([QueryKey.integration.instagramOauthState], {
  queryFn: async ({ queryKey }): Promise<InstagramOauthState> => {
    const mode = queryKey[1] === 'redirect' ? 'redirect' : 'popup';
    // Third segment opts into Instagram's re-authentication, which is the only way
    // back to the account chooser when the browser already holds a session.
    const forceReauth = queryKey[2] === true;
    return await httpClient
      .get(Endpoint.integration.instagramOauthState, {
        params: forceReauth ? { mode, force_reauth: true } : { mode },
      })
      .then(({ data }) => data);
  },
  // Single-use and short-lived: never serve a cached one to a second attempt.
  staleTime: 0,
  gcTime: 0,
});

/** Instagram — disconnect */
queryClient.setMutationDefaults([QueryKey.integration.instagramDelete], {
  mutationFn: async () => {
    await httpClient.delete(Endpoint.integration.instagramDelete);
  },
});

/** SlackV2 — OAuth URL fetch + revoke */
queryClient.setMutationDefaults([QueryKey.integration.slackV2Oauth], {
  mutationFn: async (): Promise<{ url: string }> =>
    await httpClient.get(Endpoint.integration.slackV2Oauth).then(({ data }) => data),
});

queryClient.setMutationDefaults([QueryKey.integration.slackV2Revoke], {
  mutationFn: async () => {
    await httpClient.post(Endpoint.integration.slackV2Revoke);
  },
});

/** Simulation */
queryClient.setQueryDefaults([QueryKey.simulation.datasets], {
  queryFn: async ({ queryKey: [, offset = 0, limit = 25] }: any) =>
    await httpClient
      .get(Endpoint.simulation.datasets, { params: { offset, limit } })
      .then(ApiMapper.toResponseData),
});

queryClient.setQueryDefaults([QueryKey.simulation.defaultEvaluationCriteria], {
  queryFn: async () =>
    await httpClient
      .get(Endpoint.simulation.defaultEvaluationCriteria)
      .then(ApiMapper.toResponseData),
  staleTime: Infinity,
});

queryClient.setQueryDefaults([QueryKey.simulation.recentVisitorMessages], {
  queryFn: async () =>
    await httpClient.get(Endpoint.simulation.recentVisitorMessages).then(ApiMapper.toResponseData),
});

queryClient.setQueryDefaults([QueryKey.simulation.datasetDetail], {
  queryFn: async ({ queryKey: [, datasetId] }: any) =>
    await httpClient
      .get(Endpoint.simulation.datasetDetail.replace(':datasetId', datasetId))
      .then(ApiMapper.toResponseData),
});

queryClient.setMutationDefaults([QueryKey.simulation.createDataset], {
  mutationFn: async (data: {
    name: string;
    description?: string;
    evaluation_criteria?: string;
    conversation_metadata?: Record<string, unknown>;
  }) => await httpClient.post(Endpoint.simulation.datasets, data).then(ApiMapper.toResponseData),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.simulation.datasets] });
  },
});

queryClient.setMutationDefaults([QueryKey.simulation.updateDataset], {
  mutationFn: async ({
    datasetId,
    ...data
  }: {
    datasetId: number;
    name?: string;
    description?: string;
    evaluation_criteria?: string;
    conversation_metadata?: Record<string, unknown>;
  }) =>
    await httpClient
      .patch(Endpoint.simulation.datasetDetail.replace(':datasetId', datasetId.toString()), data)
      .then(ApiMapper.toResponseData),
  onSuccess: (_data, variables) => {
    queryClient.invalidateQueries({
      queryKey: [QueryKey.simulation.datasetDetail, variables.datasetId.toString()],
    });
    queryClient.invalidateQueries({ queryKey: [QueryKey.simulation.datasets] });
  },
});

queryClient.setMutationDefaults([QueryKey.simulation.deleteDataset], {
  mutationFn: async (datasetId: number) =>
    await httpClient
      .delete(Endpoint.simulation.datasetDetail.replace(':datasetId', datasetId.toString()))
      .then(ApiMapper.toResponseData),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.simulation.datasets] });
  },
});

queryClient.setMutationDefaults([QueryKey.simulation.addMessages], {
  mutationFn: async ({ datasetId, messages }: { datasetId: number; messages: any[] }) =>
    await httpClient
      .post(Endpoint.simulation.datasetMessages.replace(':datasetId', datasetId.toString()), {
        messages,
      })
      .then(ApiMapper.toResponseData),
  onSuccess: (_data, variables: { datasetId: number; messages: any[] }) => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.simulation.datasets] });
    queryClient.invalidateQueries({
      queryKey: [QueryKey.simulation.datasetDetail, variables.datasetId.toString()],
    });
  },
});

queryClient.setMutationDefaults([QueryKey.simulation.uploadCsv], {
  mutationFn: async ({ name, file }: { name: string; file: File }) => {
    const formData = new FormData();
    formData.append('name', name);
    formData.append('file', file);
    return await httpClient
      .post(Endpoint.simulation.uploadCsv, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(ApiMapper.toResponseData);
  },
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.simulation.datasets] });
  },
});

queryClient.setQueryDefaults([QueryKey.simulation.runs], {
  queryFn: async ({ queryKey: [, datasetId, offset = 0, limit = 25] }: any) =>
    await httpClient
      .get(Endpoint.simulation.datasetRuns.replace(':datasetId', datasetId), {
        params: { offset, limit },
      })
      .then(ApiMapper.toResponseData),
});

queryClient.setQueryDefaults([QueryKey.simulation.runDetail], {
  queryFn: async ({ queryKey: [, runId] }: any) =>
    await httpClient
      .get(Endpoint.simulation.runDetail.replace(':runId', runId))
      .then(ApiMapper.toResponseData),
});

queryClient.setMutationDefaults([QueryKey.simulation.startRun], {
  mutationFn: async ({
    datasetId,
    ...data
  }: {
    datasetId: number;
    label?: string;
    config_overrides?: Record<string, unknown>;
  }) =>
    await httpClient
      .post(Endpoint.simulation.datasetRuns.replace(':datasetId', datasetId.toString()), data)
      .then(ApiMapper.toResponseData),
});

queryClient.setMutationDefaults([QueryKey.simulation.cancelRun], {
  mutationFn: async (runId: number) =>
    await httpClient
      .post(Endpoint.simulation.runCancel.replace(':runId', runId.toString()))
      .then(ApiMapper.toResponseData),
});

/** Sony admin (Quickchat-staff KB addon) */

export type SonyAdminDocumentPatchInputDto = {
  externalId: string;
  title?: string;
  object_type?: SonyObjectTypeEnum;
  locale?: string;
  view_url?: string;
  summary_text_content?: string;
  tags?: string[];
  related_models?: string[];
  countries?: string[];
};

export type SonyAdminArticlePatchInputDto = {
  externalId: string;
  title?: string;
  content?: string;
};

queryClient.setQueryDefaults([QueryKey.sonyAdmin.documents], {
  queryFn: async ({ queryKey }): Promise<SonyAdminDocumentsListDto> => {
    const [, params] = queryKey as [
      string,
      { offset: number; limit: number; filters: SonyDocumentsFilter },
    ];
    const { offset, limit, filters } = params;
    return await httpClient
      .get<SonyAdminDocumentsListDto>(Endpoint.sonyAdmin.documents, {
        params: { offset, limit, ...filters },
      })
      .then((res) => res.data);
  },
  staleTime: 0,
});

queryClient.setQueryDefaults([QueryKey.sonyAdmin.document], {
  queryFn: async ({ queryKey }): Promise<SonyAdminDocumentDetailDto> => {
    const [, externalId, includePayload] = queryKey as [string, string, boolean | undefined];
    return await httpClient
      .get<SonyAdminDocumentDetailDto>(
        Endpoint.sonyAdmin.document.replace(':externalId', externalId),
        { params: { include_payload: Boolean(includePayload) } },
      )
      .then((res) => res.data);
  },
  staleTime: 0,
  // Poll the payload-less detail while a pipeline run, retrain or live deploy is in flight.
  refetchInterval: (query) => {
    const [, , includePayload] = query.queryKey as [string, string, boolean | undefined];
    if (includePayload) return false;
    const detail = query.state.data as SonyAdminDocumentDetailDto | undefined;
    return detail?.pipeline_in_progress || detail?.retrain_in_progress || detail?.deploy_in_progress
      ? SONY_ADMIN_DETAIL_POLL_INTERVAL_MS
      : false;
  },
});

queryClient.setMutationDefaults([QueryKey.sonyAdmin.documentPatch], {
  mutationFn: async ({ externalId, ...data }: SonyAdminDocumentPatchInputDto) =>
    await httpClient
      .patch(Endpoint.sonyAdmin.documentPatch.replace(':externalId', externalId), data)
      .then((res) => res.data),
  onSuccess: (_data, { externalId }: SonyAdminDocumentPatchInputDto) => {
    toast.success(i18n.t('sony_admin:toast.document_saved'));
    void queryClient.invalidateQueries({ queryKey: [QueryKey.sonyAdmin.document, externalId] });
    void queryClient.invalidateQueries({ queryKey: [QueryKey.sonyAdmin.documents] });
  },
  onError: () => {
    toast.error(i18n.t('sony_admin:toast.document_save_failed'));
  },
});

queryClient.setMutationDefaults([QueryKey.sonyAdmin.runPipeline], {
  mutationFn: async (externalId: string) =>
    await httpClient
      .post(Endpoint.sonyAdmin.runPipeline.replace(':externalId', externalId))
      .then((res) => res.data),
  onSuccess: (_data, externalId: string) => {
    toast.success(i18n.t('sony_admin:toast.pipeline_started'));
    void queryClient.invalidateQueries({ queryKey: [QueryKey.sonyAdmin.document, externalId] });
    void queryClient.invalidateQueries({ queryKey: [QueryKey.sonyAdmin.documents] });
  },
  onError: () => {
    toast.error(i18n.t('sony_admin:toast.pipeline_failed'));
  },
});

queryClient.setMutationDefaults([QueryKey.sonyAdmin.articlePatch], {
  mutationFn: async ({ externalId, ...data }: SonyAdminArticlePatchInputDto) =>
    await httpClient
      .patch(Endpoint.sonyAdmin.article.replace(':externalId', externalId), data)
      .then((res) => res.data),
  onSuccess: (_data, { externalId }: SonyAdminArticlePatchInputDto) => {
    toast.success(i18n.t('sony_admin:toast.article_saved'));
    void queryClient.invalidateQueries({ queryKey: [QueryKey.sonyAdmin.document, externalId] });
  },
  onError: () => {
    toast.error(i18n.t('sony_admin:toast.article_save_failed'));
  },
});

queryClient.setMutationDefaults([QueryKey.sonyAdmin.deployLive], {
  mutationFn: async (externalId: string) =>
    await httpClient
      .post(Endpoint.sonyAdmin.deployLive.replace(':externalId', externalId))
      .then((res) => res.data),
  onSuccess: (_data, externalId: string) => {
    toast.success(i18n.t('sony_admin:toast.deploy_started'));
    void queryClient.invalidateQueries({ queryKey: [QueryKey.sonyAdmin.document, externalId] });
    void queryClient.invalidateQueries({ queryKey: [QueryKey.sonyAdmin.documents] });
  },
  onError: () => {
    toast.error(i18n.t('sony_admin:toast.deploy_failed'));
  },
});

queryClient.setQueryDefaults([QueryKey.channelIdentity.list], {
  queryFn: async (): Promise<LinkedChannels> =>
    await httpClient.get(Endpoint.channelIdentity.list).then((res) => res.data),
  // A link is completed on the user's phone, so this list goes stale without any
  // event this tab can see. Without this the app-wide `staleTime: Infinity` makes
  // every consumer's refetchOnMount/refetchOnWindowFocus a no-op, and a completed
  // link stays invisible — and unrevokable — until a full page reload.
  staleTime: 0,
});

queryClient.setMutationDefaults([QueryKey.channelIdentity.whatsappLink], {
  mutationFn: async (): Promise<WhatsAppLinkOffer> =>
    await httpClient.post(Endpoint.channelIdentity.whatsappLink).then((res) => res.data),
  onError: () => {
    toast.error(i18n.t('support_whatsapp:toast.link_failed'));
  },
});

queryClient.setMutationDefaults([QueryKey.channelIdentity.revoke], {
  mutationFn: async (channelIdentityId: number) =>
    await httpClient.delete(
      Endpoint.channelIdentity.revoke.replace(':channelIdentityId', String(channelIdentityId)),
    ),
  onSuccess: () => {
    toast.success(i18n.t('support_whatsapp:toast.disconnected'));
    void queryClient.invalidateQueries({ queryKey: [QueryKey.channelIdentity.list] });
  },
  onError: () => {
    toast.error(i18n.t('support_whatsapp:toast.disconnect_failed'));
  },
});
